import { useState, useMemo, useRef, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { parseJsonResponse, isTransientFetchError } from "@/lib/parse-json-response";
import { cn } from "@/lib/utils";
import type {
  EmailBlockCopy,
  EmailInstanceCopy,
  EmailLinkConfig,
  EmailUniversalAssets,
  UniversalFooterContent,
  EmailFooterOverrides,
  EmailRegenMode,
  EmailSectionRegenScope,
  EmailHeroSlant,
  EmailBodyImagePreference,
  EmailImageTreatment,
} from "@shared/schema";
import {
  emailBlockType,
  emailIsCtaBlock,
  normalizeEmailLinkConfig,
  emailLinkConfigHasUsableLink,
  type EmailCapturedBlock,
  resolveEmailInstanceCopy,
  resolveEmailBodyImagePreference,
  emailBodyImageNeedsApply,
  emailBodyHasReplaceableImage,
  resolveEmailBodyPolishBlock,
  resolveEffectiveEmailFooter,
  resolveEmailLayoutVariant,
  nextEmailLayoutVariant,
  computeEmailPolishSourceHash,
  computeEmailPolishBlockHash,
  computeEmailPolishBlockVisualHash,
  EMAIL_REGEN_LABELS,
  EMAIL_REGEN_DESCRIPTIONS,
  EMAIL_REGEN_STATUS_LABELS,
  isEmailRegenStatus,
  EMAIL_HERO_SLANTS,
  EMAIL_HERO_SLANT_LABELS,
  emailLockedImageKey,
  emailEditedBlockKey,
  orderedEmailRenderKeys,
  isEmailImageTreatmentEligible,
  resolveEmailImageTreatment,
  resolveEmailProductListImageTreatment,
  EMAIL_IMAGE_TREATMENT_MAIN_SLOT,
  productListTileSlot,
  emailVariantNeedsImage,
} from "@shared/schema";
import { friendlyPolishErrorText } from "@shared/email-polish-errors";
import {
  resolveEmailBrandStyle,
  EMAIL_FONT_LINK_HREF,
  emailBrandGoogleFamilies,
  emailBrandFontFaceCss,
  type EmailBrandStyle,
} from "@/lib/email-brand-style";
import { googleFontsCssUrl } from "@shared/google-fonts";
import { brandFontProxyUrl } from "@shared/brand-fonts";
import {
  type EmailDetail,
  FLOW_LABELS,
  BLOCK_META,
  LOCKED_KEYS,
  DEFAULT_LINK_CONFIG,
  CAPTURE_OPTS,
  sanitizeFileName,
  fetchImageDataUrl,
  uploadBlockImage,
  normalizeBlocks,
  normalizeHidden,
  linkForBlock,
  captureEmailBlocks,
  captureAndPersistEmailBlocks,
  collectEmailBlockImageUrls,
  waitForEmailAssetsSettled,
  PreviewBlock,
  renderPreviewBlock,
  EmailBlockSkeleton,
  EmailBlockFailed,
} from "@/lib/email-render";
import { useSampledFooterStyle } from "@/lib/use-sampled-footer";
import { lastFooterSampleUrl } from "@shared/email-footer-sampling";
import {
  getEmailPresetTemplate,
  normalizeEmailPresetBlocks,
  visibleEmailPresetBlocks,
  isEmailBlockDrivenPresetEmail,
  orderEmailBlockDrivenBlocks,
  normalizeEmailBlockDrivenBlocksPreserving,
} from "@shared/email-presets";
import { emailPresetSectionLabel } from "@/lib/email-preset-blocks";
import type { EmailEditor as EmailEditorCallbacks } from "@/components/email-inline-editor";
import {
  EmailSubjectPreviewPanel,
} from "@/components/email-inline-editor";
import {
  heroSwapPanelState,
  resolveHeroRegenSettle,
  heroImageActionsBusy,
} from "@/lib/emailHeroRepolish";
import { createAutosaveQueue, type AutosaveQueue } from "@/lib/emailAutosaveQueue";
import {
  EmailBlockEditPanel,
  type BlockEditState,
  type EmailBlockEditPanelProps,
} from "@/components/email-block-edit-panel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import {
  ArrowLeft,
  Mail,
  Megaphone,
  Repeat,
  Users,
  Package,
  Percent,
  Loader2,
  Image as ImageIcon,
  Type as TypeIcon,
  PanelBottom,
  PanelLeftClose,
  PanelLeftOpen,
  RefreshCw,
  AlertTriangle,
  ListChecks,
  LayoutGrid,
  Swords,
  Columns3,
  Lightbulb,
  Info,
  Flag,
  GripVertical,
  Eye,
  EyeOff,
  Trash2,
  Sparkles,
  Send,
  Copy,
  Check,
  Smartphone,
  Monitor,
  CheckCircle2,
  Download,
  Wand2,
  RotateCcw,
  Lock,
  Unlock,
  AlertCircle,
} from "lucide-react";

// Missing-link send guard copy (Task #2325): friendly, and points at the
// sidebar Links section instead of a terse "add a link" failure.
const MISSING_LINK_SEND_MESSAGE =
  "Your email needs a destination link so its buttons work. Add one in the Links section of the left sidebar — we've highlighted it for you.";
import {
  computeEmailProgress,
  type EmailProgress,
} from "@/lib/email-progress";


// Klaviyo sending is wired up: clicking "Send draft to Klaviyo" captures the
// per-block images, hosts them on R2, and creates a real template + draft
// campaign in Klaviyo.
const KLAVIYO_SEND_ENABLED = true;

// Task #1806 — polish is a user-granted beta trial. The former hardcoded kill
// switch (user decision, July 2026) is replaced by the `email_polish` beta
// grant, resolved server-side per brand (fail-closed, default OFF) and read
// inside the component from /api/brands/:brandId/beta-features. Ungranted
// brands keep the exact kill-switch behavior: the stacked live block preview
// is canonical, Send/export capture it directly, and already-polished emails
// keep showing their polished stack.

// Blocks a user can ADD to a block-driven (new-template) email. Restricted to
// the no-AI blocks introduced with the new templates — the fixed layout order
// places them, and duplicates collapse, so each is offered once at most.

// A real product review the user can feature in the Reviews block.
type ReviewChoice = { quote: string; author: string; rating: number };
const reviewKey = (r: { quote: string }) => r.quote.trim().toLowerCase();


// Trigger a browser download for a blob using the anchor-click pattern.
function downloadBlob(blob: Blob, filename: string) {
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objUrl);
}


type Payload = {
  name: string;
  subjectLineVariants: string[];
  previewText: string;
  headline: string;
  bodyCopy: string;
  ctaText: string;
  blocks: string[];
  hiddenBlocks: string[];
  blockCopy: EmailBlockCopy;
  linkConfig: EmailLinkConfig;
  footerOverrides: EmailFooterOverrides;
};

function makePayload(o: Payload): Payload {
  return {
    name: o.name,
    subjectLineVariants: o.subjectLineVariants,
    previewText: o.previewText,
    headline: o.headline,
    bodyCopy: o.bodyCopy,
    ctaText: o.ctaText,
    blocks: o.blocks,
    hiddenBlocks: o.hiddenBlocks,
    blockCopy: o.blockCopy,
    linkConfig: o.linkConfig,
    footerOverrides: o.footerOverrides,
  };
}

function payloadFromEmail(email: EmailDetail): Payload {
  return makePayload({
    name: email.name ?? "",
    subjectLineVariants: email.subjectLineVariants ?? [],
    previewText: email.previewText ?? "",
    headline: email.headline ?? "",
    bodyCopy: email.bodyCopy ?? "",
    ctaText: email.ctaText ?? "",
    // Block-driven template emails keep the template's fixed order (logo_banner
    // may lead the hero) — normalizeBlocks' hero-first scaffold would both list
    // the sidebar wrong AND persist the wrong order back on save.
    blocks: isEmailBlockDrivenPresetEmail(email)
      ? normalizeEmailBlockDrivenBlocksPreserving(
          Array.isArray(email.blocks) ? (email.blocks as string[]) : null,
        )
      : normalizeBlocks(email.blocks),
    hiddenBlocks: normalizeHidden(email.hiddenBlocks),
    blockCopy: (email.blockCopy as EmailBlockCopy) ?? {},
    linkConfig: (email.linkConfig as EmailLinkConfig) ?? DEFAULT_LINK_CONFIG,
    footerOverrides: (email.footerOverrides as EmailFooterOverrides) ?? {},
  });
}

// =============================================================================
// Page wrapper — handles the query + loading/not-found/error, then mounts the
// editor keyed by email id so all editable state initializes cleanly from the
// loaded email (no hydration race; remounts when navigating to another email).
// =============================================================================
export default function EmailEditorPage() {
  const { brandId, emailId } = useParams<{ brandId: string; emailId: string }>();

  const {
    data: email,
    isLoading,
    isError,
    error,
  } = useQuery<EmailDetail>({
    queryKey: ["/api/brands", brandId, "email-express/emails", emailId],
    enabled: !!brandId && !!emailId,
    // While a send is in flight (built + pushed to Klaviyo off-request), OR the
    // hero banner is still rendering (background worker), poll so the page flips
    // to sent/failed / shows the new hero without a manual refresh. The query-arg
    // form reads live cache data and avoids a state-init TDZ.
    refetchInterval: (query) =>
      query.state.data?.status === "sending" ||
      query.state.data?.polishStatus === "pending" ||
      query.state.data?.copyStatus === "pending" ||
      Object.values(
        (query.state.data?.blockCopyStatus ?? {}) as Record<string, string>,
      ).some((s) => s === "pending") ||
      query.state.data?.heroImageStatus === "pending" ||
      (query.state.data?.sellingPointsImages ?? []).some(
        (img) => img.status === "pending",
      ) ||
      query.state.data?.usVsThemImageStatus === "pending" ||
      query.state.data?.comparisonTableImageStatus === "pending" ||
      (query.state.data?.didYouKnowImages ?? []).some(
        (img) => img.status === "pending",
      ) ||
      // Keep polling while a scoped regeneration is flagged in-flight so the GET
      // lazy-clear runs and the per-mode status label comes back down.
      !!query.state.data?.regenStatus
        ? 4000
        : false,
  });

  const backLink = `/brands/${brandId}/email-express`;
  const isNotFound = isError && /(^|\b)404(\b|:)/.test((error as Error)?.message || "");

  if (isLoading) {
    return (
      <div className="p-8 space-y-6" data-testid="loading-editor">
        <Skeleton className="h-10 w-full" />
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr_320px] gap-4">
          <Skeleton className="h-96 w-full" />
          <Skeleton className="h-96 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (isNotFound || (!isLoading && !email)) {
    return (
      <Notice
        testId="status-email-notfound"
        title="Email not found"
        body="This email may have been deleted or doesn't belong to this brand."
        backLink={backLink}
        backTestId="link-back-notfound"
      />
    );
  }

  if (isError) {
    return (
      <Notice
        testId="status-email-error"
        title="Couldn't load this email"
        body={(error as Error)?.message || "Please try again."}
        backLink={backLink}
        backTestId="link-back-error"
      />
    );
  }

  // Deferred flow stubs have no content to edit — template selection happens
  // on the flow page. Bounce there instead of opening an empty editor.
  if (email!.copyStatus === "awaiting_template" && email!.flowId) {
    return <RedirectToFlow brandId={brandId!} flowId={email!.flowId} />;
  }

  return <EmailEditor key={email!.id} email={email!} brandId={brandId!} />;
}

function RedirectToFlow({ brandId, flowId }: { brandId: string; flowId: string }) {
  const [, navigate] = useLocation();
  useEffect(() => {
    navigate(`/brands/${brandId}/email-express/flows/${flowId}`, { replace: true });
  }, [brandId, flowId, navigate]);
  return null;
}

function Notice({
  testId,
  title,
  body,
  backLink,
  backTestId,
}: {
  testId: string;
  title: string;
  body: string;
  backLink: string;
  backTestId: string;
}) {
  const [, navigate] = useLocation();
  return (
    <div className="p-8 max-w-md mx-auto text-center space-y-4 mt-16" data-testid={testId}>
      <Mail className="h-10 w-10 text-muted-foreground mx-auto" />
      <h1 className="text-xl font-bold">{title}</h1>
      <p className="text-sm text-muted-foreground">{body}</p>
      <Button variant="outline" className="gap-2" onClick={() => navigate(backLink)} data-testid={backTestId}>
        <ArrowLeft className="h-4 w-4" /> Back to Email Express
      </Button>
    </div>
  );
}

// =============================================================================
// Generation progress bar (preview panel)
// -----------------------------------------------------------------------------
// A thin bar pinned to the top of the preview panel that tracks REAL draft
// progress (computed from persisted status fields — never a timer). It mounts
// only while a draft is actively generating, smoothly fills to 100% on
// completion then fades out, and stops + reddens on a copy-stage failure.
// =============================================================================

// Lighten a #hex toward white by `t` (0..1). Used to derive a soft brand-tinted
// shimmer for the skeletons + a readable bar fill.
function tintHexToWhite(hex: string, t: number): string {
  const v = /^#([0-9a-f]{6})$/i.test(hex.trim()) ? hex.trim() : "#1e293b";
  const r = parseInt(v.slice(1, 3), 16);
  const g = parseInt(v.slice(3, 5), 16);
  const b = parseInt(v.slice(5, 7), 16);
  const mix = (c: number) => Math.round(c + (255 - c) * t);
  const h = (n: number) => n.toString(16).padStart(2, "0");
  return `#${h(mix(r))}${h(mix(g))}${h(mix(b))}`;
}

function EmailGenerationProgress({
  email,
  primaryColor,
}: {
  email: EmailDetail;
  primaryColor: string;
}) {
  const progress: EmailProgress = useMemo(
    () => computeEmailProgress(email),
    [email],
  );

  // Only ever surface the bar if the draft was actively generating during this
  // mount — so opening a long-finished email never flashes a fading bar.
  const everActiveRef = useRef(false);
  if (progress.state !== "complete") everActiveRef.current = true;

  const [show, setShow] = useState(true);
  const [fading, setFading] = useState(false);
  // Smoothly-animated displayed percentage. The real per-stage percent jumps in
  // discrete steps, so we ease a separate `displayed` value toward it every
  // frame — the counter never jumps between milestones and (via a gentle trickle
  // once it catches up) never hard-stalls while a stage is in flight.
  const [displayed, setDisplayed] = useState(0);

  // Latest real signals, read by the rAF loop without re-subscribing.
  const targetRef = useRef(0);
  const stateRef = useRef(progress.state);
  targetRef.current = progress.percent;
  stateRef.current = progress.state;

  // Detect the leading edge into "generating" — a fresh full generation OR a
  // regeneration (single block / full email). Reset to 0% so every run fills
  // from the start and never carries over the previous run's progress.
  const prevStateRef = useRef<EmailProgress["state"] | null>(null);
  useEffect(() => {
    const prev = prevStateRef.current;
    if (progress.state === "generating" && prev !== "generating") {
      setDisplayed(0);
      setShow(true);
      setFading(false);
    }
    prevStateRef.current = progress.state;
  }, [progress.state]);

  // Continuous easing loop — both the fill width and the counter read `displayed`.
  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      setDisplayed((d) => {
        // Never animate a bar that won't surface (a long-finished email).
        if (!everActiveRef.current) return d;
        const st = stateRef.current;
        let goal: number;
        if (st === "complete") {
          goal = 100;
        } else if (st === "error") {
          goal = d; // freeze in place
        } else {
          // generating: chase the real target; once caught up, trickle slowly
          // toward a sub-100 ceiling so the bar keeps inching forward.
          const t = targetRef.current;
          goal = d < t ? t : Math.max(t, Math.min(t + 12, 94));
        }
        let next = d + (goal - d) * (1 - Math.exp(-dt * 3.4));
        if (st === "complete" && next > 99.5) next = 100;
        return Math.max(0, Math.min(100, next));
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const isError = progress.state === "error";
  const reachedDone = progress.state === "complete" && displayed >= 99.5;

  // Completion: hold the full bar briefly, then fade out smoothly (never abrupt).
  useEffect(() => {
    if (!reachedDone) return;
    const t1 = setTimeout(() => setFading(true), 1200);
    const t2 = setTimeout(() => setShow(false), 1800);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [reachedDone]);

  // Terminal error: keep the amber bar visible while any copy/image work is
  // still in flight, but once everything has settled hold it briefly (long
  // enough to register the failure) then fade out — the underlying failures
  // still surface via per-block retry skeletons / brand-color image fallbacks.
  const errorSettled = isError && !progress.pending;
  useEffect(() => {
    if (!errorSettled) return;
    const t1 = setTimeout(() => setFading(true), 2400);
    const t2 = setTimeout(() => setShow(false), 3000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [errorSettled]);

  if (!everActiveRef.current || !show) return null;

  const pct = Math.round(displayed);
  const fillColor = isError ? "#d97706" : primaryColor; // amber-600 on error
  // Stage-specific status that tracks the live generation phase (voice brief →
  // subject lines → hero copy → sections → images), plus the model's terminal
  // "Draft ready" / "Something went wrong" labels.
  const phaseText = progress.phaseLabel;

  return (
    <div
      className={`pointer-events-none sticky top-0 z-30 self-stretch -mx-6 border-b bg-background/95 shadow-sm backdrop-blur transition-opacity duration-500 ${
        fading ? "opacity-0" : "opacity-100"
      }`}
      data-testid="email-generation-progress"
      data-progress-state={progress.state}
      data-progress-percent={pct}
    >
      <div className="h-2.5 w-full overflow-hidden bg-muted">
        <div
          className="h-full"
          style={{ width: `${displayed}%`, backgroundColor: fillColor }}
        />
      </div>
      <div className="flex items-center gap-2 px-6 py-1.5">
        <span
          className={`text-xs font-semibold ${
            isError ? "text-amber-700" : "text-foreground"
          }`}
          data-testid="text-progress-phase"
        >
          {phaseText}
        </span>
        <span
          className="ml-auto text-xs font-bold tabular-nums"
          style={{ color: fillColor }}
          data-testid="text-progress-percent"
        >
          {pct}%
        </span>
      </div>
    </div>
  );
}

// =============================================================================
// The three-panel editor.
// =============================================================================
function EmailEditor({ email, brandId }: { email: EmailDetail; brandId: string }) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const backLink = `/brands/${brandId}/email-express`;

  // Consume the full brand row (the GET /api/brands/:id endpoint already returns
  // it) so the brand-style resolver can theme the preview from real brand data.
  // Task #1806 — `email_polish` beta grant (fail-closed: false while loading
  // or on error, so an ungranted user never kicks off polish).
  const { data: emailBetaFlags } = useQuery<Record<string, boolean>>({
    queryKey: [`/api/brands/${brandId}/beta-features`],
    enabled: !!brandId,
  });
  const emailAutoPolishEnabled = emailBetaFlags?.email_polish === true;

  const { data: brand } = useQuery<Record<string, any>>({
    queryKey: ["/api/brands", brandId],
    enabled: !!brandId,
  });
  // The persisted Brand Styling layer (server-managed) re-dresses the resolved
  // brand style via bounded tokens. Null/absent → byte-identical to before.
  const style = useMemo<EmailBrandStyle>(
    () =>
      resolveEmailBrandStyle(
        brand ?? {},
        (email as any)?.brandStyleDirection ?? null,
      ),
    [brand, (email as any)?.brandStyleDirection],
  );
  const brandName = style.brandName;

  // Real product reviews available to feature in the Reviews block. Mirrors the
  // Landing Page Express review picker: the user features which real reviews to
  // show (and how many) rather than hand-typing them.
  const { data: rawReviews } = useQuery<any[]>({
    queryKey: ["/api/products", email.productId, "reviews"],
    enabled: !!email.productId,
  });
  const availableReviews = useMemo<ReviewChoice[]>(
    () =>
      (Array.isArray(rawReviews) ? rawReviews : [])
        .filter((r) => r?.reviewText && String(r.reviewText).trim())
        .map((r) => ({
          quote: String(r.reviewText).trim(),
          author: (r.reviewer && String(r.reviewer).trim()) || "Verified buyer",
          rating:
            typeof r.rating === "number" && r.rating > 0 ? Math.min(5, Math.round(r.rating)) : 5,
        })),
    [rawReviews],
  );

  // The footer renders from the email's snapshotted Universal footer asset when
  // present (snapshotted at generate time), and degrades gracefully otherwise.
  const footerContent = (email.universalAssets?.footer?.content ??
    null) as UniversalFooterContent | null;

  // Brand-level footer settings (Task #1899): physical address + unsubscribe
  // link that win over the snapshot/overrides for those two fields, so the
  // preview matches the sent MJML byte-for-byte.
  const { data: brandFooterSettings = null } = useQuery<{
    physicalAddress?: string;
    unsubscribeUrl?: string;
  } | null>({
    queryKey: [`/api/brands/${brandId}/email-express/settings`],
    enabled: !!brandId,
  });

  // Ensure the built-in heading/body web fonts are available to the preview.
  useEffect(() => {
    const id = "email-express-brand-fonts";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = EMAIL_FONT_LINK_HREF;
    document.head.appendChild(link);
  }, []);

  // Additionally load the brand's SAVED fonts (Design System step) so the
  // preview renders the exact families the resolver picked — Google families via
  // the stylesheet API, uploads via @font-face from R2 (through the same-origin
  // font proxy, since R2 sends no CORS headers). Re-runs when the brand's saved
  // fonts change; no-op for brands without saved fonts.
  useEffect(() => {
    const googleFamilies = emailBrandGoogleFamilies(style);
    const faceCss = emailBrandFontFaceCss(style, brandFontProxyUrl);
    if (googleFamilies.length === 0 && !faceCss) return;

    const nodes: HTMLElement[] = [];
    if (googleFamilies.length > 0) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = googleFontsCssUrl(googleFamilies);
      document.head.appendChild(link);
      nodes.push(link);
    }
    if (faceCss) {
      const styleEl = document.createElement("style");
      styleEl.textContent = faceCss;
      document.head.appendChild(styleEl);
      nodes.push(styleEl);
    }
    return () => nodes.forEach((n) => n.remove());
  }, [style]);

  // ---- Editable state (initialized once from the loaded email) --------------
  const init = useMemo(() => payloadFromEmail(email), [email.id]);
  const [name, setName] = useState(init.name);
  const [subjectLines, setSubjectLines] = useState<string[]>(init.subjectLineVariants);
  const [previewText, setPreviewText] = useState(init.previewText);
  const [headline, setHeadline] = useState(init.headline);
  const [bodyCopy, setBodyCopy] = useState(init.bodyCopy);
  const [ctaText, setCtaText] = useState(init.ctaText);
  const [blocks, setBlocks] = useState<string[]>(init.blocks);
  const [hiddenBlocks, setHiddenBlocks] = useState<string[]>(init.hiddenBlocks);
  const [blocksPanelCollapsed, setBlocksPanelCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem("email-blocks-collapsed") === "1";
    } catch {
      return false;
    }
  });
  const setBlocksCollapsed = (v: boolean) => {
    setBlocksPanelCollapsed(v);
    try {
      localStorage.setItem("email-blocks-collapsed", v ? "1" : "0");
    } catch {}
  };
  const [blockCopy, setBlockCopy] = useState<EmailBlockCopy>(init.blockCopy);
  // Satori-baked preview blocks (hero headline band + did-you-know feature
  // tiles) rendered server-side from the CURRENT editable text so the in-canvas
  // preview shows the same pixel-locked typography the sent email embeds. Keyed
  // like the send worker's `rasterizedBlocks` ("hero_band" + instance ids).
  const [rasterizedBlocks, setRasterizedBlocks] = useState<Record<string, string>>({});
  const [linkConfig, setLinkConfig] = useState<EmailLinkConfig>(init.linkConfig);
  // Per-email footer edits (social links / disclaimer / unsubscribe URL). Durable
  // and merged over the snapshot at render time; the copy worker never touches it.
  const [footerOverrides, setFooterOverrides] = useState<EmailFooterOverrides>(
    init.footerOverrides,
  );
  const [status, setStatus] = useState(email.status);
  // Keep the local status mirror in sync with the polled query data so a send
  // that finishes off-request (sent_to_klaviyo / send_failed) updates the badge
  // and Send button without a manual refresh.
  useEffect(() => {
    setStatus(email.status);
  }, [email.status]);

  // ---- Progressive copy load (background worker) ----------------------------
  // While the background copy worker is still filling this email in
  // (copyStatus === "pending"), the server is the source of truth: re-seed the
  // editable copy state from each poll so blocks fill in live, and keep
  // lastSavedRef in lockstep so the autosave effect treats the seed as
  // already-saved (no spurious save races with the worker's writes). We also
  // seed ONCE on the pending → ready transition so the final block's copy (which
  // arrives in the same response that flips copyStatus to "ready") isn't lost.
  // Legacy emails (copyStatus null) never enter this branch and behave as before.
  const prevCopyStatusRef = useRef<string | null>(email.copyStatus ?? null);
  useEffect(() => {
    const wasPending = prevCopyStatusRef.current === "pending";
    const isPending = email.copyStatus === "pending";
    prevCopyStatusRef.current = email.copyStatus ?? null;
    if (isPending || (wasPending && email.copyStatus === "ready")) {
      const p = payloadFromEmail(email);
      lastSavedRef.current = JSON.stringify(p);
      setName(p.name);
      setSubjectLines(p.subjectLineVariants);
      setPreviewText(p.previewText);
      setHeadline(p.headline);
      setBodyCopy(p.bodyCopy);
      setCtaText(p.ctaText);
      setBlocks(p.blocks);
      setHiddenBlocks(p.hiddenBlocks);
      setBlockCopy(p.blockCopy);
      setLinkConfig(p.linkConfig);
      setFooterOverrides(p.footerOverrides);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  // ---- Preset-template gating (Task #975) -----------------------------------
  // Preset emails render deterministically client-side: the raw preview IS the
  // canonical view (always on-screen), the AI polish pipeline never runs (the
  // server 400s any polish POST for them), and sends always ride the
  // captured-blocks path. Locked template sections stay pinned; unlocked ones
  // keep the existing drag-to-reorder + hide affordances (never delete).
  const isPreset = !!email.presetTemplateKey;
  // Block-driven template emails (Task #1032, "Perfect Pair") use the STANDARD
  // blocks AND the standard polish pipeline — the editor experience is
  // identical to a freeform email (polish canonical, whole-email loader,
  // polished send). Only their LAYOUT is fixed (no drag-reorder / delete /
  // layout-regen). presetDef stays null so every block list / label / reorder
  // path takes the standard branch. `isLegacyPreset` marks the OLD
  // preset-sections emails (Task #975) that render deterministically and
  // never polish.
  const isBlockDriven = isEmailBlockDrivenPresetEmail(email);
  const isLegacyPreset = isPreset && !isBlockDriven;
  const presetDef =
    isPreset && !isBlockDriven
      ? getEmailPresetTemplate(email.presetTemplateKey)
      : null;
  const presetLockedKeys = useMemo(
    () =>
      new Set(
        presetDef
          ? presetDef.sections
              .filter((s) => s.locked)
              .map((s) => s.key as string)
          : [],
      ),
    [presetDef],
  );

  // ---- UI-only state --------------------------------------------------------
  const [selectedBlock, setSelectedBlock] = useState<string>("hero");
  // Block instance ids the user explicitly asked to (re)generate in THIS session
  // (per-block Regenerate / Apply changes). No longer drives a per-block preview
  // spinner — the whole-email loader covers the preview now; it only marks the
  // block's edit-panel button as a disabled "Regenerating…" to block a
  // double-fire. Cleared when the run settles.
  const [regeneratingKeys, setRegeneratingKeys] = useState<string[]>([]);
  // Optimistic "a partial (queued) re-render is in flight" flag. Set the instant
  // a per-block regenerate / Apply-changes / restitch is triggered — BEFORE the
  // save+POST round-trip flips polishStatus to "pending" — so the whole-email
  // loader shows immediately (no dead window). Cleared in the mutation's
  // onSettled, after which `!polished` (pending) keeps the loader up until ready.
  const [partialPending, setPartialPending] = useState(false);
  // Soft guard before export/send when the user has un-regenerated per-block
  // edits: the polished image they'd export/send is STALE for those sections.
  // We don't block — just warn and offer to apply first. Holds the action to
  // run if they choose "Continue anyway".
  const [pendingActionGuard, setPendingActionGuard] = useState<
    null | (() => void)
  >(null);
  const [previewWidth, setPreviewWidth] = useState<"desktop" | "mobile">("mobile");
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<"saved" | "saving" | "error">("saved");
  const [isExporting, setIsExporting] = useState(false);
  // Polish-disabled preview: each block is captured client-side as a retina
  // PNG (uploaded to R2 + persisted on the row — the SAME images the send
  // embeds into Klaviyo), and that image stack is the canonical preview.
  const [localCaptured, setLocalCaptured] = useState<EmailCapturedBlock[] | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [captureFailed, setCaptureFailed] = useState(false);
  const captureBusyRef = useRef(false);
  const lastCaptureHashRef = useRef<string | null>(null);
  // Image regens overwrite a FIXED R2 key (e.g. emails/<id>/hero-2x.png), so
  // the content hash CANNOT detect a finished image regen — the URL never
  // changes. Track the pending→ready transition explicitly and treat it as a
  // queued change so the amber banner stays up (enabled) and the user can
  // "Update preview" to render the fresh image. Cleared on a successful
  // capture. (User decision Jul 29 2026: updates stay MANUAL, never silent.)
  const [imageRefreshQueued, setImageRefreshQueued] = useState(false);
  const prevImagePendingRef = useRef(false);
  // Captured-preview emails: an image-scope "Regenerate this section" click is
  // QUEUED (nothing runs yet) — the user approves by clicking "Update preview",
  // which fires the regen(s) and then auto-recaptures once the new image lands
  // (the approval already happened, so no second confirmation is needed).
  // (User decision Jul 29 2026: regenerate = queue; Update preview = approve.)
  const [queuedSectionRegens, setQueuedSectionRegens] = useState<
    Array<{ block: string; scope: "image"; feedback?: string }>
  >([]);
  const autoCaptureAfterRegenRef = useRef(false);
  // Content hash of the email as first seen this session — staleness baseline
  // for legacy captured previews that carry no persisted sourceHash.
  const initialEmailHashRef = useRef<string | null>(null);
  // The polished image stack is the CANONICAL view. The raw editor is always
  // mounted (parked off-screen so a re-polish can capture it) and is only shown
  // on-screen in explicit "Edit draft" mode. Defaults to canonical (editMode
  // false) so the user never passively sees the un-polished email or per-block
  // skeletons.
  const [editMode, setEditMode] = useState(false);
  // Local polish state machine. "running" covers the capture+POST window before
  // the server flips polishStatus to "pending". `polishWanted` is the immediate
  // intent (shows the single loader the instant a layout change happens, before
  // the debounced runner fires). `polishKick` is bumped to fire the runner.
  // `polishLocalError` surfaces a client-side capture failure (server failures
  // come through email.polishStatus === "failed").
  const [polishLocal, setPolishLocal] = useState<"idle" | "running">("idle");
  const [polishWanted, setPolishWanted] = useState(false);
  const [polishKick, setPolishKick] = useState(0);
  const [polishLocalError, setPolishLocalError] = useState<string | null>(null);
  const autoPolishHashRef = useRef<string | null>(null);
  const layoutDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [logoExportUrl, setLogoExportUrl] = useState<string | null>(null);
  const hasUsableLink = emailLinkConfigHasUsableLink(linkConfig);
  const [sendError, setSendError] = useState<string | null>(null);
  // Missing-link send guard (Task #2325): remember where the CTA link input
  // lives so the guard's "Go to link field" action can scroll/focus/highlight
  // it — the field sits low in the left sidebar and users don't find it.
  const linkInputRef = useRef<HTMLInputElement | null>(null);
  const [linkFieldHighlight, setLinkFieldHighlight] = useState(false);
  const linkHighlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const focusLinkField = () => {
    setBlocksCollapsed(false); // the field lives in the blocks sidebar
    setLinkFieldHighlight(true);
    if (linkHighlightTimerRef.current) clearTimeout(linkHighlightTimerRef.current);
    linkHighlightTimerRef.current = setTimeout(
      () => setLinkFieldHighlight(false),
      2600,
    );
    // Wait a tick so a collapsed sidebar can re-render before we scroll.
    setTimeout(() => {
      linkInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      linkInputRef.current?.focus({ preventScroll: true });
    }, 60);
  };
  const previewRef = useRef<HTMLDivElement>(null);
  const previewPaneRef = useRef<HTMLElement>(null);
  const [previewPaneW, setPreviewPaneW] = useState(0);
  // Data URL for the hero photo, embedded during PNG export so the cross-origin
  // R2 image isn't blank in the capture (mirrors logoExportUrl).
  const [heroExportUrl, setHeroExportUrl] = useState<string | null>(null);
  const [usVsThemExportUrl, setUsVsThemExportUrl] = useState<string | null>(null);
  const [comparisonTableExportUrl, setComparisonTableExportUrl] = useState<
    string | null
  >(null);
  const [productExportUrls, setProductExportUrls] = useState<string[]>([]);
  const [productImageExportUrls, setProductImageExportUrls] = useState<string[]>(
    [],
  );
  // Data URLs for per-instance selected block images (image_only /
  // image_discount, Task #2286), keyed by SOURCE url, embedded during PNG
  // export so cross-origin chosen/uploaded images aren't blank in the capture.
  const [blockImageExportUrls, setBlockImageExportUrls] = useState<
    Record<string, string>
  >({});
  // Data URLs for ready selling-points images, keyed by block instance id,
  // embedded during PNG export so the cross-origin R2 images aren't blank.
  const [sellingPointsExportUrls, setSellingPointsExportUrls] = useState<
    Record<string, string>
  >({});
  // Data URLs for ready did-you-know images (W8), keyed by block instance id,
  // embedded during PNG export so the cross-origin R2 images aren't blank.
  const [didYouKnowExportUrls, setDidYouKnowExportUrls] = useState<
    Record<string, string>
  >({});
  // Preset-template capture assets: AI image slots keyed by SECTION id, and raw
  // real-photo URLs referenced by preset copy (category-grid rows, the
  // second-product snapshot) mapped url→dataUrl. Same capture contract as above.
  const [presetExportUrls, setPresetExportUrls] = useState<
    Record<string, string>
  >({});
  const [presetUrlExportUrls, setPresetUrlExportUrls] = useState<
    Record<string, string>
  >({});

  // The sent email is a fixed-width design (600px desktop / 390px mobile). Render
  // the preview at that exact design width and scale-to-fit the available pane so
  // it stays WYSIWYG with the email instead of reflowing/squishing. During export
  // we force scale 1 so html-to-image captures at full resolution.
  useEffect(() => {
    const el = previewPaneRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setPreviewPaneW(e.contentRect.width);
    });
    ro.observe(el);
    setPreviewPaneW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  const previewDesignW = previewWidth === "mobile" ? 390 : 600;
  const previewScale = isExporting
    ? 1
    : previewPaneW > 0
      ? Math.min(1, (previewPaneW - 2) / previewDesignW)
      : 1;

  const currentPayload = useMemo(
    () =>
      makePayload({
        name,
        subjectLineVariants: subjectLines,
        previewText,
        headline,
        bodyCopy,
        ctaText,
        blocks,
        hiddenBlocks,
        blockCopy,
        linkConfig,
        footerOverrides,
      }),
    [name, subjectLines, previewText, headline, bodyCopy, ctaText, blocks, hiddenBlocks, blockCopy, linkConfig, footerOverrides],
  );

  // Live ref to the current payload so the debounced / auto polish runner (which
  // fires from effects outside the render that built it) always captures the
  // latest layout + copy instead of a stale closure.
  const currentPayloadRef = useRef(currentPayload);
  useEffect(() => {
    currentPayloadRef.current = currentPayload;
  });

  const lastSavedRef = useRef<string>(JSON.stringify(init));
  const emailQueryKey = ["/api/brands", brandId, "email-express/emails", email.id];
  // Mutation responses return the raw email row WITHOUT the GET route's derived,
  // non-persisted fields (productImageUrls / productListImages, computed live
  // from the product). Writing such a response straight into the cache would
  // strip them and make the product-list block vanish after a regen/save. Merge
  // those fields from the prior cache (they only depend on the stable productId).
  const writeEmailCache = (updated: EmailDetail) =>
    queryClient.setQueryData(emailQueryKey, (prev: EmailDetail | undefined) => {
      if (!prev) return updated;
      // Task #2489: monotonic guard — never let an OLDER server row (a slow
      // response landing after a newer one, e.g. a save racing a hero swap)
      // overwrite fresher cached state; that visibly snapped the editor back
      // to stale content. Every write path bumps updatedAt server-side.
      const prevTs = Date.parse(String((prev as any).updatedAt ?? ""));
      const nextTs = Date.parse(String((updated as any).updatedAt ?? ""));
      if (Number.isFinite(prevTs) && Number.isFinite(nextTs) && nextTs < prevTs) {
        return prev;
      }
      const merged = { ...updated } as Record<string, unknown>;
      for (const k of ["productImageUrls", "productListImages"] as const) {
        if (merged[k] === undefined && (prev as Record<string, unknown>)[k] !== undefined) {
          merged[k] = (prev as Record<string, unknown>)[k];
        }
      }
      return merged as EmailDetail;
    });

  // Task #2489: serialized autosave. One PATCH in flight at a time; edits made
  // while a save runs coalesce into a single follow-up save that rebuilds its
  // payload from the LIVE state ref at fire time (so a headline edit right
  // after a block-image pick always persists post-image state). Each save
  // sends the row's last-known updatedAt as an optimistic-concurrency base;
  // on a 409 conflict (another writer bumped the row — e.g. a background
  // worker) it retries with a fresh base + fresh live payload.
  const performSavePatch = async (payload: Payload): Promise<{ updated: EmailDetail; payload: Payload }> => {
    let body: Payload = payload;
    let base: string | null =
      ((queryClient.getQueryData(emailQueryKey) as EmailDetail | undefined) as any)
        ?.updatedAt ?? null;
    for (let attempt = 0; ; attempt++) {
      const res = await fetch(
        `/api/brands/${brandId}/email-express/emails/${email.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(base ? { ...body, baseUpdatedAt: base } : body),
        },
      );
      if (res.status === 409) {
        const data = await res.json().catch(() => ({}));
        if (data?.conflict && attempt < 2) {
          // Another writer bumped the row. Our local state is still the
          // user's truth for the editable fields — retry with the fresh base
          // and the FRESHEST live payload.
          base = typeof data.currentUpdatedAt === "string" ? data.currentUpdatedAt : null;
          body = currentPayloadRef.current;
          continue;
        }
        throw new Error(data?.message || "Save conflicted with another update — please retry");
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.message || `Save failed (${res.status})`);
      }
      return { updated: await parseJsonResponse<EmailDetail>(res), payload: body };
    }
  };

  // The queue is created once (stable identity) but must always call the
  // freshest performSavePatch closure.
  const performSavePatchRef = useRef(performSavePatch);
  performSavePatchRef.current = performSavePatch;

  const saveQueueRef = useRef<AutosaveQueue | null>(null);
  if (!saveQueueRef.current) {
    saveQueueRef.current = createAutosaveQueue<Payload, { updated: EmailDetail; payload: Payload }>({
      getPayload: () => currentPayloadRef.current,
      save: (payload) => performSavePatchRef.current(payload),
      onSaved: ({ updated, payload }, _p, isLatest) => {
        lastSavedRef.current = JSON.stringify(payload);
        // Stale-response guard: while a newer save is queued, this response is
        // already superseded — writing it into the cache would snap visible
        // state back; drop it and let the follow-up save's response land.
        if (isLatest) {
          writeEmailCache(updated);
          queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/email-express/emails`] });
          queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/email-express/stats`] });
          setSaveState("saved");
        }
      },
      onError: (e: any) => {
        setSaveState("error");
        toast({ title: "Save failed", description: e?.message || "Try again", variant: "destructive" });
      },
    });
  }
  const saveQueue = saveQueueRef.current;

  // Debounced autosave: whenever the editable payload diverges from the last
  // saved snapshot, persist it after a short pause. Skips while a name is empty
  // (the API requires a name).
  useEffect(() => {
    const serialized = JSON.stringify(currentPayload);
    if (serialized === lastSavedRef.current) {
      if (saveState !== "error") setSaveState("saved");
      return;
    }
    if (!name.trim()) {
      setSaveState("error");
      return;
    }
    setSaveState("saving");
    const handle = setTimeout(() => saveQueue.request(), 650);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPayload]);

  // Debounced rasterization of the Satori preview bands. Fires whenever the
  // headline / did-you-know copy / block set changes, posting the live (possibly
  // unsaved) text so the preview's hero band + feature tiles match the sent
  // email's baked typography. Best-effort: on any failure the preview keeps the
  // last bands and the renderer falls back to live-CSS for absent keys.
  useEffect(() => {
    // Preset emails have no Satori-baked archetype bands — skip entirely.
    if (email.presetTemplateKey) return;
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const res = await fetch(
          `/api/brands/${brandId}/email-express/emails/${email.id}/preview-blocks`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ headline, blockCopy, blocks, hiddenBlocks }),
            credentials: "include",
          },
        );
        if (!res.ok || cancelled) return;
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        setRasterizedBlocks(
          data?.rasterizedBlocks && typeof data.rasterizedBlocks === "object"
            ? data.rasterizedBlocks
            : {},
        );
      } catch {
        // Non-fatal: preview falls back to live-CSS bands.
      }
    }, 650);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headline, blockCopy, blocks, hiddenBlocks, brandId, email.id]);

  // ---- Regenerate -----------------------------------------------------------
  const regenMutation = useMutation({
    mutationFn: async ({
      block,
      feedback,
      mode,
      scope,
      heroSlant,
      background,
    }: {
      block: string | null;
      feedback?: string;
      // Task #863: scoped regeneration modes. Omitting `mode` keeps the legacy
      // behaviour (server infers "section" when a block is given, else "content").
      mode?: EmailRegenMode;
      scope?: EmailSectionRegenScope;
      // "Regenerate hero" only: optional emphasis steering the hero copy + image.
      heroSlant?: EmailHeroSlant;
      // Background section run on a POLISHED per-block email — the add-section
      // copy fill AND the sidebar "Regenerate this section" scopes: the
      // polished preview stays up (no whole-email loader) and the server keeps
      // the existing polish — the section lands in the queued-changes banner
      // instead of triggering a full re-render.
      background?: boolean;
    }) => {
      const body: Record<string, unknown> = {};
      if (block) body.block = block;
      if (feedback && feedback.trim()) body.feedback = feedback.trim();
      if (mode) body.mode = mode;
      if (scope) body.scope = scope;
      if (heroSlant) body.heroSlant = heroSlant;
      if (background) body.preservePolish = true;
      // Use raw fetch so we can handle 422 (missing-input) distinctly from
      // other errors without apiRequest's throwIfResNotOk losing the body.
      const res = await fetch(
        `/api/brands/${brandId}/email-express/emails/${email.id}/regenerate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
          credentials: "include",
        },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data?.missingInput) {
          const err = new Error(
            data.message ||
              "The workflow is missing required inputs. Check that all brand fields are filled in.",
          );
          (err as any).missingInput = true;
          throw err;
        }
        if (data?.retry) {
          const err = new Error(data.message || "Hero copy generation failed. Please try again.");
          (err as any).retry = true;
          throw err;
        }
        // 409 = the targeted section/hero is locked or hand-edited; the server
        // refuses to overwrite it. Surface a clear, non-destructive message.
        if (res.status === 409 || data?.protected) {
          const err = new Error(
            data.message ||
              "This section is locked or has hand-edited copy, so it was left untouched. Unlock it or clear your edits to regenerate.",
          );
          (err as any).protectedSection = true;
          throw err;
        }
        throw new Error(data?.message || `Regeneration failed (${res.status})`);
      }
      return await parseJsonResponse<EmailDetail>(res);
    },
    onSuccess: (updated, variables) => {
      // Background section run (add-section fill OR queued section regen): the
      // user may have kept editing while it ran (the preview stays up), so
      // DON'T overwrite the whole local state — merge in only the targeted
      // section's generated copy and refresh the cached row (image/status +
      // stale-stamped polish manifest fields). The queued-changes banner
      // surfaces the render step; no generic "Regenerated" toast.
      if (variables?.background && variables.block) {
        const key = variables.block;
        const isSectionRegen = variables.mode === "section";
        // The section may have been DELETED while its copy generated in the
        // background (add → delete). Nothing to merge or announce — stay
        // silent; the deletion path deliberately skipped the re-stitch too.
        if (!blocks.includes(key)) {
          writeEmailCache(updated);
          return;
        }
        const serverPayload = payloadFromEmail(updated);
        const serverCopy = serverPayload.blockCopy;
        setBlockCopy((prev) => {
          const next: typeof prev = {
            ...prev,
            instances: {
              ...(prev.instances || {}),
              [key]:
                serverCopy.instances?.[key] ??
                resolveEmailInstanceCopy(serverCopy, key),
            },
          };
          // A queued section regen may ALSO rewrite this block's punchline
          // and (scope "everything"/"layout") its layout variant — merge ONLY
          // this block's entries so concurrent edits elsewhere are kept.
          if (isSectionRegen) {
            const punch = serverCopy.punchlines?.[key];
            if (punch !== undefined) {
              next.punchlines = { ...(prev.punchlines || {}), [key]: punch };
            }
            // `variants` is the untyped back-compat layout map (the renderer
            // reads the instance's layoutVariant, merged above) — carry it so
            // the next autosave doesn't drop the server's entry.
            const variant = (serverCopy as any).variants?.[key];
            if (variant !== undefined) {
              (next as any).variants = {
                ...((prev as any).variants || {}),
                [key]: variant,
              };
            }
          }
          return next;
        });
        // Hero/body queued regens rewrite top-level scalars — merge those too
        // so the pending hash + the eventual Apply render the fresh copy.
        if (isSectionRegen && variables.scope !== "image" && variables.scope !== "layout") {
          if (key === "hero") {
            setHeadline(serverPayload.headline);
            setPreviewText(serverPayload.previewText);
            setCtaText(serverPayload.ctaText);
          }
          if (key === "body") setBodyCopy(serverPayload.bodyCopy);
        }
        writeEmailCache(updated);
        queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/email-express/emails`] });
        toast(
          isSectionRegen
            ? {
                title: "Section regenerated",
                description:
                  "The polished email is untouched — use Apply my edits to render the update into it.",
              }
            : {
                title: "Section ready",
                description:
                  "Its content has been written — use Apply my edits to render it into the polished email.",
              },
        );
        return;
      }
      const p = payloadFromEmail(updated);
      lastSavedRef.current = JSON.stringify(p);
      setName(p.name);
      setSubjectLines(p.subjectLineVariants);
      setPreviewText(p.previewText);
      setHeadline(p.headline);
      setBodyCopy(p.bodyCopy);
      setCtaText(p.ctaText);
      setBlocks(p.blocks);
      setHiddenBlocks(p.hiddenBlocks);
      setBlockCopy(p.blockCopy);
      setLinkConfig(p.linkConfig);
      setFooterOverrides(p.footerOverrides);
      writeEmailCache(updated);
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/email-express/emails`] });
      setSaveState("saved");
      const mode = variables?.mode;
      toast({
        title: mode ? EMAIL_REGEN_LABELS[mode] : "Regenerated",
        description:
          mode && EMAIL_REGEN_DESCRIPTIONS[mode]
            ? EMAIL_REGEN_DESCRIPTIONS[mode]
            : "Copy has been refreshed.",
      });
    },
    onError: (e: any) => {
      const isInput = (e as any)?.missingInput;
      const isRetry = (e as any)?.retry;
      const isProtected = (e as any)?.protectedSection;
      // retry = Sim call failed but current hero copy was preserved — warn, don't use destructive variant
      toast({
        title: isInput
          ? "Missing workflow inputs"
          : isRetry
            ? "Couldn't generate new hero copy"
            : isProtected
              ? "Section is locked or edited"
              : "Regeneration failed",
        description: e?.message || "Try again",
        variant: isRetry || isProtected ? "default" : "destructive",
      });
    },
  });
  const regeneratingAll = regenMutation.isPending && !regenMutation.variables?.block;
  // Task #863: the persisted per-mode status (survives refresh; lazily cleared by
  // the server once the async work settles) drives the button's loading label.
  const regenStatusLabel = isEmailRegenStatus(email.regenStatus)
    ? EMAIL_REGEN_STATUS_LABELS[email.regenStatus]
    : null;

  // Task #863: lock / unlock a section so scoped regeneration leaves it alone.
  const lockBlockMutation = useMutation({
    mutationFn: async ({ block, locked }: { block: string; locked: boolean }) => {
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/email-express/emails/${email.id}/lock-block`,
        { block, locked },
      );
      return await parseJsonResponse<EmailDetail>(res);
    },
    onSuccess: (updated, variables) => {
      writeEmailCache(updated);
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/email-express/emails`],
      });
      toast({
        title: variables.locked ? "Section locked" : "Section unlocked",
        description: variables.locked
          ? "This section will be preserved when you regenerate."
          : "This section can be regenerated again.",
      });
    },
    onError: (e: any) =>
      toast({
        title: "Couldn't update lock",
        description: e?.message || "Try again",
        variant: "destructive",
      }),
  });

  // Task #863: clear the "hand-edited" markers for a section so it opts back into
  // "Regenerate content" and section regeneration.
  const clearEditsMutation = useMutation({
    mutationFn: async ({ block }: { block: string }) => {
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/email-express/emails/${email.id}/clear-edits`,
        { block },
      );
      return await parseJsonResponse<EmailDetail>(res);
    },
    onSuccess: (updated) => {
      writeEmailCache(updated);
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/email-express/emails`],
      });
      toast({
        title: "Edits cleared",
        description: "This section can now be regenerated with fresh copy.",
      });
    },
    onError: (e: any) =>
      toast({
        title: "Couldn't clear edits",
        description: e?.message || "Try again",
        variant: "destructive",
      }),
  });

  // Task #863: "Start over" (full regen) is destructive — it replaces the user's
  // edits — so it goes through a confirm dialog. Task #974: the confirm dialog
  // also carries an optional feedback string (blank = identical behavior).
  const [startOverOpen, setStartOverOpen] = useState(false);
  const [startOverFeedback, setStartOverFeedback] = useState("");
  // Task #974: top-level Regenerate menu (Content / Layout / Images) opens a
  // small optional-feedback dialog before firing the mode regen. Blank
  // feedback keeps the exact pre-feedback behavior.
  const [regenModePrompt, setRegenModePrompt] = useState<
    "content" | "layout" | "images" | null
  >(null);
  const [regenModeFeedback, setRegenModeFeedback] = useState("");
  // ---- Retry background copy (progressive load) -----------------------------
  // When the background copy worker failed (copyStatus / a block status
  // "failed"), this resets the whole email's copy state to pending and re-kicks
  // the worker. The polling refetchInterval then fills the blocks back in.
  const retryCopyMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/email-express/emails/${email.id}/retry-copy`,
      );
      return await parseJsonResponse<EmailDetail>(res);
    },
    onSuccess: (updated) => {
      writeEmailCache(updated);
      queryClient.invalidateQueries({
        queryKey: [`/api/brands/${brandId}/email-express/emails`],
      });
    },
    onError: (e: any) => {
      toast({
        title: "Couldn't retry generation",
        description: e?.message || "Try again",
        variant: "destructive",
      });
    },
  });

  // ---- Subject-line regenerate (W2 Subject Lines Sim workflow) ---------------
  // Seed the count from the current variant count when it's a valid 2/4/6,
  // otherwise default to 4.
  const [subjectRegenCount, setSubjectRegenCount] = useState<string>(() =>
    [2, 4, 6].includes(init.subjectLineVariants.length)
      ? String(init.subjectLineVariants.length)
      : "4",
  );
  const [subjectFeedback, setSubjectFeedback] = useState("");
  const [subjectModalOpen, setSubjectModalOpen] = useState(false);
  const subjectRegenMutation = useMutation({
    mutationFn: async ({ count, feedback }: { count: number; feedback?: string }) => {
      const body: Record<string, unknown> = {
        count,
        currentCopy: JSON.stringify(subjectLines),
      };
      if (feedback && feedback.trim()) body.feedback = feedback.trim();
      const res = await fetch(
        `/api/brands/${brandId}/email-express/emails/${email.id}/subject-lines/regenerate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
          credentials: "include",
        },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data?.missingInput) {
          const err = new Error(
            data.message ||
              "The workflow is missing required inputs. Check that all brand fields are filled in.",
          );
          (err as any).missingInput = true;
          throw err;
        }
        if (data?.retry) {
          const err = new Error(data.message || "Subject line generation failed. Please try again.");
          (err as any).retry = true;
          throw err;
        }
        throw new Error(data?.message || `Subject line regeneration failed (${res.status})`);
      }
      return await parseJsonResponse<EmailDetail>(res);
    },
    onSuccess: (updated) => {
      const p = payloadFromEmail(updated);
      // Only the subject lines change; merge them into local state without
      // clobbering unsaved edits to other fields.
      setSubjectLines(p.subjectLineVariants);
      writeEmailCache(updated);
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/email-express/emails`] });
      toast({ title: "Subject lines regenerated" });
    },
    onError: (e: any) => {
      const isInput = (e as any)?.missingInput;
      const isRetry = (e as any)?.retry;
      toast({
        title: isInput
          ? "Missing workflow inputs"
          : isRetry
            ? "Couldn't generate new subject lines"
            : "Subject line regeneration failed",
        description: e?.message || "Try again",
        variant: isRetry ? "default" : "destructive",
      });
    },
  });

  // ---- Hero image actions (inline editor) -----------------------------------
  // Re-roll the AI hero banner with optional art-direction feedback. The route
  // flips heroImageStatus → "pending" and renders in the background; the query's
  // refetchInterval polls until it's ready, so we just merge the returned email.
  const heroImageRegenMutation = useMutation({
    mutationFn: async (feedback?: string) => {
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/email-express/emails/${email.id}/hero/regenerate`,
        feedback && feedback.trim() ? { feedback: feedback.trim() } : {},
      );
      return await parseJsonResponse<EmailDetail>(res);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(emailQueryKey, (prev: EmailDetail | undefined) => ({
        ...(prev as EmailDetail),
        ...updated,
      }));
      // Task #2329: once this async render settles ready, the polled-row
      // effect queues a hero-only re-polish so the polished composite (and
      // therefore the sent email) reflects the new AI hero.
      heroRegenAwaitingRepolishRef.current = true;
    },
    onError: (e: any) =>
      toast({
        title: "Couldn't regenerate hero image",
        description: e?.message || "Try again",
        variant: "destructive",
      }),
  });

  // After a hero swap (choose/upload) lands while the polished stack is the
  // canonical view, the polished hero is now STALE — but per user request the
  // re-polish is NOT automatic: the panel shows an explicit "Apply new image"
  // button and nothing regenerates until the user clicks it. The pending state
  // is the SERVER-persisted `heroSwapPending` flag (stamped by /hero/set and
  // the hero worker, cleared by the polish worker only after a successful hero
  // re-render), so it survives reloads and a failed apply brings the prompt
  // back instead of silently sending the stale hero. `regenerateBlocks` is
  // defined later in this component — the closure resolves it at call time.
  const applyHeroSwap = () => {
    // The user picked this exact image — the polished hero must use it as the
    // background verbatim (deterministic text-overlay re-composite), not ask
    // the model to render a new photo. The server clears heroSwapPending only
    // when the run finishes successfully.
    regenerateBlocks(["hero"], undefined, { useCurrentHeroImage: true });
  };

  // The AI hero regenerate is ASYNC — the route flips heroImageStatus to
  // "pending" and the worker renders in the background while the email query
  // polls. Arm this flag when the regen is accepted; the effect below watches
  // the polled row and queues the hero-only re-polish exactly once when the
  // render settles ready (cleared on any terminal state, so a failed render
  // or a second poll tick can never double-fire).
  const heroRegenAwaitingRepolishRef = useRef(false);
  useEffect(() => {
    const { repolish, clearAwaiting } = resolveHeroRegenSettle(
      heroRegenAwaitingRepolishRef.current,
      email,
      editMode,
    );
    if (clearAwaiting) heroRegenAwaitingRepolishRef.current = false;
    // The user explicitly clicked "Regenerate hero image", so applying the
    // result IS the requested action — composite the freshly generated
    // heroImageUrl verbatim rather than letting polish render yet another one.
    // (The successful run clears the server-side heroSwapPending flag; if the
    // apply fails, the flag stays and the manual Apply prompt takes over.)
    if (repolish)
      regenerateBlocks(["hero"], undefined, { useCurrentHeroImage: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email.heroImageStatus, email.heroImageUrl]);

  // Point the hero at an explicit, already-durable URL (a chosen product photo
  // or a freshly uploaded file). The route stores it and flips status → "ready"
  // so the preview and the send reuse exactly this image.
  const heroSetMutation = useMutation({
    mutationFn: async (url: string) => {
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/email-express/emails/${email.id}/hero/set`,
        { url },
      );
      return await parseJsonResponse<EmailDetail>(res);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(emailQueryKey, (prev: EmailDetail | undefined) => ({
        ...(prev as EmailDetail),
        ...updated,
      }));
      toast({ title: "Hero image updated" });
    },
    onError: (e: any) =>
      toast({
        title: "Couldn't update hero image",
        description: e?.message || "Try again",
        variant: "destructive",
      }),
  });

  // Upload a custom file to durable storage, then set it as the hero.
  const heroUploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      const up = await fetch("/api/upload", {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      if (!up.ok) {
        const d = await up.json().catch(() => ({}));
        throw new Error(d?.message || "Upload failed");
      }
      const { url } = (await up.json()) as { url: string };
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/email-express/emails/${email.id}/hero/set`,
        { url },
      );
      return await parseJsonResponse<EmailDetail>(res);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(emailQueryKey, (prev: EmailDetail | undefined) => ({
        ...(prev as EmailDetail),
        ...updated,
      }));
      toast({ title: "Hero image uploaded" });
    },
    onError: (e: any) =>
      toast({
        title: "Couldn't upload hero image",
        description: e?.message || "Try again",
        variant: "destructive",
      }),
  });

  // Upload a custom image for an image-only block or Body Copy: durable R2
  // upload, then a plain instance-copy patch (local draft state + autosave).
  // Deliberately NO server side-effects — swapping the image only stages it.
  const blockImageUploadMutation = useMutation({
    mutationFn: async ({
      blockKey,
      file,
      slot,
    }: {
      blockKey: string;
      file: File;
      slot?: string;
    }) => {
      const fd = new FormData();
      fd.append("file", file);
      const up = await fetch("/api/upload", {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      if (!up.ok) {
        const d = await up.json().catch(() => ({}));
        throw new Error(d?.message || "Upload failed");
      }
      const { url } = (await up.json()) as { url: string };
      return { blockKey, url, slot };
    },
    onSuccess: ({ blockKey, url, slot }) => {
      const type = emailBlockType(blockKey);
      if (type === "body") {
        patchInstanceCopy(blockKey, {
          bodyImage: { mode: "selected", url },
        });
        toast({ title: "Body Copy image selected" });
      } else if (isEmailImageTreatmentEligible(type) && slot) {
        patchImageTreatment(blockKey, slot, { mode: "selected", url });
        toast({ title: "Section image selected" });
      } else {
        patchInstanceCopy(blockKey, { imageUrl: url });
        toast({ title: "Block image updated" });
      }
    },
    onError: (e: any) =>
      toast({
        title: "Couldn't upload image",
        description: e?.message || "Try again",
        variant: "destructive",
      }),
  });

  // Central hero-image busy: includes the SERVER-side pending render (the async
  // AI worker owns the hero after a regenerate is accepted, long after the
  // mutation settles). Every hero action surface derives from this so a
  // choose/upload can never race a pending worker render — a late worker
  // completion would silently overwrite the explicit selection without a
  // re-polish, splitting the polished preview from the sent email.
  const heroImageBusy = heroImageActionsBusy({
    regenPending: heroImageRegenMutation.isPending,
    setPending: heroSetMutation.isPending,
    uploadPending: heroUploadMutation.isPending,
    heroImageStatus: email.heroImageStatus,
  });
  // In-canvas hero editing callbacks, gated to the editor only (never passed on
  // the export/send paths) so editing chrome can't leak into the sent email.
  const heroEditor: EmailEditorCallbacks = {
    onEditHeadline: setHeadline,
    onRegenerateHeroCopy: (feedback) =>
      regenMutation.mutate({ block: "hero", feedback }),
    isRegeneratingHeroCopy:
      regenMutation.isPending && regenMutation.variables?.block === "hero",
    onRegenerateHeroImage: (feedback) =>
      heroImageRegenMutation.mutate(feedback || undefined),
    onUploadHeroImage: (file) => heroUploadMutation.mutate(file),
    onChooseHeroPhoto: (url) => heroSetMutation.mutate(url),
    productPhotos: email.productImageUrls ?? [],
    heroImageBusy,
    // In-canvas editing for body, CTA, and section blocks. Body/CTA/punchline
    // and per-instance copy write straight to local draft state (autosaved);
    // per-block Regenerate reuses the same regenerate route as the panel, with
    // optional free-text feedback threaded into the block's regen prompt.
    // Chrome is gated on editor presence, never reaching the MJML.
    onEditBodyCopy: setBodyCopy,
    onEditCtaText: setCtaText,
    onEditPunchline: setInstancePunchline,
    onPatchInstanceCopy: patchInstanceCopy,
    onRegenerateBlock: (key, feedback) =>
      regenMutation.mutate({ block: key, feedback: feedback || undefined }),
    regeneratingBlockKey:
      regenMutation.isPending && regenMutation.variables?.block
        ? regenMutation.variables.block
        : null,
    // Cycle a block to its next layout variant (visual only). Reads the current
    // resolved variant from local draft state and writes the next one — the
    // preview re-renders immediately and the change autosaves like any edit.
    onChangeLayout: (key) => {
      const type = emailBlockType(key);
      const current = resolveEmailLayoutVariant(type, resolveEmailInstanceCopy(blockCopy, key));
      patchInstanceCopy(key, { layoutVariant: nextEmailLayoutVariant(type, current) });
    },
    // Per-instance image picker on image_only / image_discount blocks
    // (Task #2286). Choose/reset go through onPatchInstanceCopy directly.
    onUploadBlockImage: (blockKey, file) =>
      blockImageUploadMutation.mutate({ blockKey, file }),
    blockImageBusyKey: blockImageUploadMutation.isPending
      ? blockImageUploadMutation.variables?.blockKey ?? null
      : null,
    availableReviews: availableReviews.map((r) => ({
      quote: r.quote,
      author: r.author,
      rating: r.rating,
    })),
    // In-canvas footer editing. Each callback writes a per-email override into
    // local draft state (autosaved + persisted on `footerOverrides`); the merge
    // helper folds these over the snapshot at render time, so a generate/restyle
    // never clobbers them. The unsubscribe URL is editable but never deletable.
    footer: {
      onSetSocialLinks: (links) =>
        setFooterOverrides((p) => ({ ...p, socialLinks: links })),
      onSetDisclaimer: (text) =>
        setFooterOverrides((p) => ({
          ...p,
          disclaimerText: text,
          disclaimerDeleted: false,
        })),
      onDeleteDisclaimer: () =>
        setFooterOverrides((p) => ({ ...p, disclaimerDeleted: true })),
      onSetUnsubscribeUrl: (url) =>
        setFooterOverrides((p) => ({ ...p, unsubscribeUrl: url })),
      onSetPreferencesLabel: (label) =>
        setFooterOverrides((p) => ({
          ...p,
          preferencesLabel: label,
          preferencesDeleted: false,
        })),
      onSetPreferencesUrl: (url) =>
        setFooterOverrides((p) => ({ ...p, preferencesUrl: url })),
      onDeletePreferences: () =>
        setFooterOverrides((p) => ({ ...p, preferencesDeleted: true })),
      onSetContactLabel: (label) =>
        setFooterOverrides((p) => ({
          ...p,
          contactLabel: label,
          contactDeleted: false,
        })),
      onSetContactUrl: (url) =>
        setFooterOverrides((p) => ({ ...p, contactUrl: url })),
      onDeleteContact: () =>
        setFooterOverrides((p) => ({ ...p, contactDeleted: true })),
    },
  };

  // ---- Send to Klaviyo ------------------------------------------------------
  const sendMutation = useMutation({
    mutationFn: async () => {
      setSendError(null);
      // Require at least one usable link so the draft's CTAs aren't dead.
      if (!emailLinkConfigHasUsableLink(linkConfig)) {
        throw new Error(MISSING_LINK_SEND_MESSAGE);
      }
      // The polished image blocks ARE what gets sent. Copy text edits don't auto
      // re-polish, so (re)polish fresh here to bake the latest copy/layout into
      // the blocks before dispatching. The Send button is disabled while a polish
      // is already in flight, so we only get here from a settled state. If the
      // fresh polish fails, fall through to the server's MJML render (it branches
      // on polishStatus) rather than blocking the send entirely.
      // Queued-edit philosophy: a STALE polish (pending per-block edits) does NOT
      // trigger an automatic full re-polish here — the editor surfaces an "Apply
      // changes" affordance + a send-time warning so the user decides. We only
      // force a polish when none exists yet or the last one errored.
      // When the send can't ride the polished image stack, the client captures
      // the preview sections itself (one retina PNG per visible block —
      // pixel-identical to what's on screen) and hands them to the server,
      // which persists them BEFORE claiming the async send.
      let capturedForSend: EmailCapturedBlock[] | null = null;
      if (isLegacyPreset) {
        // Preset-template emails NEVER polish — the deterministic preview is
        // captured client-side and sent as-is. The server refuses to fall back
        // to the archetype MJML build for presets, so a capture failure here
        // must surface as a send error (never a silent wrong email).
        await saveQueue.flush();
        const fresh = await waitForEmailAssetsSettled(brandId, email.id);
        capturedForSend = await captureEmailBlocks({
          brandId,
          email: fresh,
          brand,
        });
      } else if (!emailAutoPolishEnabled && (!polished || polishErrored)) {
        // Polish disabled: capture the live stacked preview directly (same
        // path preset emails use) — never kick a polish from the send.
        await saveQueue.flush();
        const fresh = await waitForEmailAssetsSettled(brandId, email.id);
        capturedForSend = await captureEmailBlocks({
          brandId,
          email: fresh,
          brand,
        });
      } else if (!polished || polishErrored) {
        if (layoutDebounceRef.current) clearTimeout(layoutDebounceRef.current);
        setPolishWanted(false);
        try {
          await runPolish({ awaitReady: true, silent: true });
        } catch {
          toast({
            title: "Sending without polish",
            description:
              "We couldn't finish the polished version, so we're sending the standard preview capture.",
          });
          try {
            // runPolish saves before POSTing, but if it failed before its save
            // the row could still hold stale edits — flush the live payload so
            // the capture renders exactly what the user sees.
            await saveQueue.flush();
            const fresh = await waitForEmailAssetsSettled(brandId, email.id);
            capturedForSend = await captureEmailBlocks({
              brandId,
              email: fresh,
              brand,
            });
          } catch (captureErr: any) {
            // Capture is best-effort here: the server falls back to its legacy
            // hybrid build when no captures exist (logged server-side).
            console.warn("[email-editor] preview capture failed", captureErr);
          }
        }
      }
      // The server builds the deliverable off-request: polished emails send
      // their canonical image stack; otherwise the captured preview sections
      // are compiled to a stacked-image email (legacy emails with neither fall
      // back once to the old hybrid MJML build).
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/email-express/emails/${email.id}/send`,
        capturedForSend ? { blocks: capturedForSend } : {},
      );
      return await parseJsonResponse<
        EmailDetail & {
          klaviyo?: {
            mode:
              | "campaign"
              | "template"
              | "flow"
              | "flow_template"
              | "pending"
              | "done";
            message: string;
          };
        }
      >(res);
    },
    onSuccess: (updated) => {
      setSendError(null);
      setStatus(updated.status);
      const { klaviyo, ...email } = updated;
      queryClient.setQueryData(emailQueryKey, email);
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/email-express/emails`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/email-express/stats`] });
      // The build + Klaviyo push now runs off-request. A successful POST just
      // means the send was accepted and the email is being built; the page
      // polls for the terminal state and a notification fires on completion.
      if (updated.status === "sending" || klaviyo?.mode === "pending") {
        toast({
          title: "Sending to Klaviyo…",
          description:
            klaviyo?.message ||
            "Building your email and sending it to Klaviyo — you'll get a notification when it's ready.",
        });
        return;
      }
      const isFlow = klaviyo?.mode === "flow";
      const isTemplate =
        klaviyo?.mode === "template" || klaviyo?.mode === "flow_template";
      toast({
        title: isFlow
          ? "Draft flow created in Klaviyo"
          : isTemplate
            ? "Saved to Klaviyo as a template"
            : "Sent to Klaviyo",
        description:
          klaviyo?.message ||
          (isFlow
            ? "A draft flow was created in Klaviyo — review the trigger and turn it on."
            : "A draft campaign was created in Klaviyo."),
      });
    },
    onError: (e: any) => {
      const msg = e?.message || "Try again";
      setSendError(msg);
      if (msg === MISSING_LINK_SEND_MESSAGE) {
        // Friendlier missing-link guard (Task #2325): explain why the link is
        // needed, point at the field, and jump the user straight to it.
        focusLinkField();
        toast({
          title: "Add a link first",
          description: msg,
          variant: "destructive",
          action: (
            <ToastAction altText="Go to link field" onClick={focusLinkField}>
              Go to link field
            </ToastAction>
          ),
        });
        return;
      }
      toast({ title: "Send failed", description: msg, variant: "destructive" });
    },
  });

  // ---- Duplicate ------------------------------------------------------------
  const dupMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/email-express/emails/${email.id}/duplicate`,
        {},
      );
      return await parseJsonResponse<EmailDetail>(res);
    },
    onSuccess: (copy) => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/email-express/emails`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/email-express/stats`] });
      toast({ title: "Email duplicated", description: copy.name });
      navigate(`/brands/${brandId}/email-express/${copy.id}`);
    },
    onError: (e: any) => {
      toast({ title: "Duplicate failed", description: e?.message || "Try again", variant: "destructive" });
    },
  });

  // ---- Delete ---------------------------------------------------------------
  // Standalone emails use the standalone delete route; flow-member emails go
  // through the flow delete route so the remaining sequence is re-numbered.
  // On success navigate back to the flow page (flow member) or the dashboard.
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (email.flowId) {
        await apiRequest(
          "DELETE",
          `/api/brands/${brandId}/email-express/flows/${email.flowId}/emails/${email.id}`,
        );
      } else {
        await apiRequest(
          "DELETE",
          `/api/brands/${brandId}/email-express/emails/${email.id}`,
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/email-express/emails`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/email-express/flows`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/email-express/stats`] });
      queryClient.removeQueries({
        queryKey: ["/api/brands", brandId, "email-express/emails", email.id],
      });
      toast({ title: "Email deleted" });
      navigate(
        email.flowId
          ? `/brands/${brandId}/email-express/flows/${email.flowId}`
          : backLink,
      );
    },
    onError: (e: any) => {
      setConfirmingDelete(false);
      toast({ title: "Delete failed", description: e?.message || "Try again", variant: "destructive" });
    },
  });

  // Flow member emails are sent together as one chained draft flow from the
  // flow sequence view — never individually here. Hide the per-email send.
  const isFlowMember = email.type === "flow";
  const isSent = status === "sent_to_klaviyo";
  const isSending = status === "sending";

  // ---- Polish (the canonical render) ----------------------------------------
  // A ready polish's image blocks ARE the email the user sees / downloads /
  // sends. The raw editor only appears in explicit "Edit draft" mode. While a
  // polish is in flight — or content is still generating — one whole-email loader
  // covers the preview; no half-built / raw email or per-block skeletons are ever
  // shown passively.
  const polished =
    email.polishStatus === "ready" && (email.polishBlocks?.length ?? 0) > 0;
  const polishing = email.polishStatus === "pending";
  const polishFailed = email.polishStatus === "failed";
  // Hash of the CURRENT LOCAL content (copy + layout) layered over the server's
  // image URLs — the exact shape the server fingerprints at polish time. We hash
  // local state (not the cached `email` row) so staleness reflects unsaved edits
  // IMMEDIATELY: the row lags a debounced autosave, and the Send path relies on
  // this to decide whether to re-polish before dispatching.
  const localPolishHash = useMemo(
    () =>
      computeEmailPolishSourceHash({
        ...(email as any),
        headline,
        bodyCopy,
        ctaText,
        blockCopy,
        blocks,
        hiddenBlocks,
        linkConfig,
        footerOverrides,
      }),
    [
      email,
      headline,
      bodyCopy,
      ctaText,
      blockCopy,
      blocks,
      hiddenBlocks,
      linkConfig,
      footerOverrides,
    ],
  );
  // The email has been edited since it was last polished: the live (local) hash
  // no longer matches the hash captured at polish time. Drives the non-blocking
  // "edits will apply on layout change or send" note. Copy text edits do NOT
  // auto re-polish (stale-in-polished is intentional).
  const polishStale =
    polished &&
    !!email.polishSourceHash &&
    localPolishHash !== email.polishSourceHash;
  // A polish error from either side: server-reported failure OR a client-side
  // capture/upload failure. Surfaces the failed+retry card instead of an
  // infinite loader.
  const polishErrored = polishFailed || !!polishLocalError;
  // The canonical preview is "loading" (show the single loader, never the raw
  // email) whenever we're not in Edit-draft mode and there isn't a finished
  // polish to show: covers copy generation, the gap before auto-polish fires,
  // the local capture/POST window, the layout-change debounce, and the server
  // polish itself.
  // The Regenerate dropdown must surface the SAME whole-email loading view the
  // instant it's clicked — not just a small button spinner. The server later
  // flips polishStatus to keep the loader up through the fresh render; this
  // pending flag covers the in-flight request window before that lands.
  // EXCEPT a background add-section fill: that one keeps the polished preview
  // visible (the queued-changes banner surfaces the new section instead).
  const toolbarRegenBusy =
    regenMutation.isPending && !regenMutation.variables?.background;
  // A background section run is in flight — an add-section copy fill (addBlock)
  // OR a queued sidebar "Regenerate this section" on a polished email. The
  // preview stays up; the queued-changes banner shows a "still generating" note
  // and gates Apply until the section's content has landed. If the user deletes
  // the section while it's still generating, the run no longer matters — drop
  // the flag so no banner/gating lingers for a block that's gone.
  const backgroundFillBusy =
    regenMutation.isPending &&
    !!regenMutation.variables?.background &&
    !!regenMutation.variables.block &&
    blocks.includes(regenMutation.variables.block);
  // A SCOPED partial run: a single-/few-block regen on a per-block manifest.
  // Instead of the opaque whole-email loader, the polished stack STAYS on
  // screen with a loading overlay on only the targeted section(s), so the
  // user can keep browsing/editing the other sections. The manifest survives
  // a partial claim (a full invalidation nulls polishBlocks), and the targeted
  // blocks are known from the local in-flight keys plus the server's "stale"
  // contentHash sentinels. Restitch-only runs (no keys), full regens, and the
  // initial polish keep the whole-email loader exactly as before.
  // A non-queued sidebar section regen (image/copy/everything WITHOUT
  // preservePolish) is also block-scoped since the server started scoping its
  // polish invalidation to the edited block — cover its request window too so
  // the preview doesn't flash the whole-email loader before the server's
  // pending status (with the "stale" sentinel) lands.
  const sectionRegenBusy =
    regenMutation.isPending &&
    !regenMutation.variables?.background &&
    !!regenMutation.variables?.block;
  const scopedRunKeys = useMemo(() => {
    const keys = new Set<string>(regeneratingKeys);
    if (sectionRegenBusy && regenMutation.variables?.block) {
      keys.add(regenMutation.variables.block);
    }
    if (polishing) {
      (email.polishBlocks ?? []).forEach((b) => {
        if (b.blockKey && b.contentHash === "stale") keys.add(b.blockKey);
      });
    }
    return keys;
  }, [
    regeneratingKeys,
    sectionRegenBusy,
    regenMutation.variables?.block,
    polishing,
    email.polishBlocks,
  ]);
  const scopedPartialRun =
    !editMode &&
    polishLocal !== "running" &&
    !(toolbarRegenBusy && !sectionRegenBusy) &&
    (polishing || partialPending || sectionRegenBusy) &&
    scopedRunKeys.size > 0 &&
    (email.polishBlocks?.length ?? 0) > 0 &&
    (email.polishBlocks ?? []).every((b) => !!b.blockKey);
  // Polish-disabled canonical preview: the captured per-block PNGs (fresh
  // capture this session, else the persisted row captures from a prior one).
  const capturedList: EmailCapturedBlock[] =
    localCaptured ??
    ((Array.isArray((email as any).capturedBlocks)
      ? ((email as any).capturedBlocks as EmailCapturedBlock[])
      : []) || []);
  const canonicalLoading =
    !editMode &&
    !polishErrored &&
    (isLegacyPreset
      ? // Preset emails never polish: the live preview is canonical. Only a
        // whole-email regenerate covers it with the loader (per-section
        // generation shows the usual per-block skeletons instead).
        toolbarRegenBusy
      : !emailAutoPolishEnabled && !polished
        ? // Polish disabled: loading until the first block capture lands (the
          // preview is ALWAYS images — each one is what gets sent to Klaviyo).
          toolbarRegenBusy || (capturedList.length === 0 && !captureFailed)
        : polishLocal === "running" ||
        polishWanted ||
        ((partialPending || toolbarRegenBusy || !polished) &&
          !scopedPartialRun));
  // The read-only polished image stack is the canonical view when a polish is
  // ready and nothing is (re)rendering — AND during a SCOPED partial run,
  // where it stays up with a loading overlay on just the targeted section(s).
  // Any other run (full regen, initial polish, restitch) still shows the
  // whole-email PolishingCard (canonicalLoading).
  const showPolishedStack =
    !editMode &&
    !polishErrored &&
    ((polished && !canonicalLoading) || scopedPartialRun);
  const polishBusy = polishLocal === "running" || polishing || partialPending;
  // The raw (editable) preview is ON-SCREEN in explicit Edit-draft mode and
  // ALWAYS for preset emails (their deterministic render is the canonical
  // view) — except while the whole-email loader covers a preset regenerate.
  // With polish disabled, the raw HTML preview only shows on-screen as a
  // last-resort fallback (capture failed, nothing persisted) — the canonical
  // view is the captured image stack, matching what Klaviyo receives.
  const showCapturedStack =
    !emailAutoPolishEnabled &&
    !editMode &&
    !polished &&
    !isLegacyPreset &&
    !canonicalLoading &&
    capturedList.length > 0;
  // "Queued changes": local edits (block add/remove/reorder, copy tweak) are
  // saving or saved-but-not-yet-recaptured, so the captured image stack on
  // screen is behind the draft. Mirrors V1's polish "Apply changes" — the
  // preview does NOT auto-refresh; an amber banner offers "Update preview".
  // The capture-time hash persisted on the first captured block covers edits
  // made before a reload; legacy captures without it read as fresh.
  const capturedSourceHash =
    lastCaptureHashRef.current ??
    (capturedList[0] as EmailCapturedBlock | undefined)?.sourceHash ??
    null;
  const currentEmailHash = computeEmailPolishSourceHash(email as any);
  // Legacy captures (persisted before the sourceHash stamp) can't be compared
  // across reloads — fall back to a session baseline: the email's hash when
  // this editor mounted. Any in-session change then reads as queued.
  if (initialEmailHashRef.current === null) {
    initialEmailHashRef.current = currentEmailHash;
  }
  const previewQueued =
    !capturing &&
    (imageRefreshQueued ||
      queuedSectionRegens.length > 0 ||
      saveState === "saving" ||
      (capturedSourceHash !== null
        ? capturedSourceHash !== currentEmailHash
        : currentEmailHash !== initialEmailHashRef.current));
  // Bottom-edge footer sampling for the polished/captured preview stacks: the
  // sent stacked-image email paints the appended live footer with the average
  // color of the bottom edge of the LAST block image (see the send path's
  // sampleImageBottomColor). Mirror it here so the preview footer flows out of
  // the image stack exactly like the send. Same "last image" rule as the
  // server: last non-footer block with a url. Null (not showing / sampling
  // failed) keeps the brand-style page background fallback.
  const polishedLastImageUrl = showPolishedStack
    ? lastFooterSampleUrl(
        ((email.polishBlocks ?? []) as Array<{ blockKey?: string | null; url?: string | null }>).filter(
          (b) => b.blockKey !== "footer" && b.blockKey !== "preset_trust",
        ),
      )
    : null;
  const capturedLastImageUrl = showCapturedStack
    ? lastFooterSampleUrl(
        capturedList.filter(
          (b) => b.blockKey !== "footer" && b.blockKey !== "preset_trust",
        ),
      )
    : null;
  const polishedSampledFooter = useSampledFooterStyle(polishedLastImageUrl);
  const capturedSampledFooter = useSampledFooterStyle(capturedLastImageUrl);
  const showRawPreview =
    editMode ||
    ((isLegacyPreset ||
      (!emailAutoPolishEnabled && !polished && capturedList.length === 0)) &&
      !canonicalLoading);

  // Disable the Send button while a send is in flight or already complete, while
  // the canonical render is still loading, or while ANY polish is in flight
  // (including in Edit-draft mode, where canonicalLoading is false) so a click
  // can't race a running polish into a duplicate/concurrent capture. Send always
  // (re)polishes from this settled state; a send_failed email stays sendable so
  // the user can retry.
  const sendDisabled =
    !KLAVIYO_SEND_ENABLED ||
    isFlowMember ||
    isSent ||
    isSending ||
    sendMutation.isPending ||
    canonicalLoading ||
    polishBusy ||
    // A preview-image capture is uploading — let it settle so the send's own
    // capture can't race it.
    capturing ||
    // Preset emails (and any unpolished email while polish is disabled) send a
    // client capture of the preview — block the send while the structured copy
    // is still being written.
    ((isLegacyPreset || (!emailAutoPolishEnabled && !polished)) &&
      email.copyStatus === "pending");

  // Embed cross-origin (R2) brand/hero/product images as same-origin data URLs
  // so they render in an html-to-image capture instead of going blank. Awaits a
  // repaint so the embedded URLs are painted before any capture runs. Shared by
  // the PNG export and the Polish capture.
  const embedExportImages = async () => {
    // Embed the brand logo + hero photo as data URLs up-front so they render
    // in the capture (cross-origin/R2 images would otherwise be blank).
    // Proxied if needed.
    if (style.logoUrl) {
      const dataUrl = await fetchImageDataUrl(style.logoUrl);
      if (dataUrl) setLogoExportUrl(dataUrl);
    }
    // Only embed the hero when it's the `ready` persisted photo — matches the
    // preview's status gate so the exported PNG can't show a stale image that
    // a pending/failed regen has superseded.
    if (email.heroImageStatus === "ready" && email.heroImageUrl) {
      const dataUrl = await fetchImageDataUrl(email.heroImageUrl);
      if (dataUrl) setHeroExportUrl(dataUrl);
    }
    // Same status gate for the us-vs-them comparison image so the export can't
    // capture a stale visual a pending/failed regen has superseded.
    if (email.usVsThemImageStatus === "ready" && email.usVsThemImageUrl) {
      const dataUrl = await fetchImageDataUrl(email.usVsThemImageUrl);
      if (dataUrl) setUsVsThemExportUrl(dataUrl);
    }
    // Same status gate for the comparison-table supporting image.
    if (
      email.comparisonTableImageStatus === "ready" &&
      email.comparisonTableImageUrl
    ) {
      const dataUrl = await fetchImageDataUrl(email.comparisonTableImageUrl);
      if (dataUrl) setComparisonTableExportUrl(dataUrl);
    }
    // Bundle product-list block: embed each member SKU's real photo. Keep the
    // array index-aligned with productListImages (fall back to the live URL per
    // slot) so the export swap maps to the correct tile.
    const productUrls = (email.productListImages ?? []).map((m) => m.imageUrl);
    if (productUrls.length) {
      const dataUrls = await Promise.all(
        productUrls.map(async (u) => (await fetchImageDataUrl(u)) || u),
      );
      setProductExportUrls(dataUrls);
    }
    // Range product-list block: embed the product's own available photos,
    // index-aligned with productImageUrls so the export swap maps correctly.
    const rangeUrls = email.productImageUrls ?? [];
    if (rangeUrls.length) {
      const dataUrls = await Promise.all(
        rangeUrls.map(async (u) => (await fetchImageDataUrl(u)) || u),
      );
      setProductImageExportUrls(dataUrls);
    }
    // Per-instance selected block images (image_only / image_discount,
    // Task #2286): embed each chosen/uploaded image keyed by SOURCE url so the
    // export swap matches the preview's resolution.
    const blockImgUrls = collectEmailBlockImageUrls(blocks, blockCopy);
    if (blockImgUrls.length) {
      const entries = await Promise.all(
        blockImgUrls.map(
          async (u) => [u, (await fetchImageDataUrl(u)) || u] as const,
        ),
      );
      setBlockImageExportUrls(Object.fromEntries(entries));
    }
    // Embed each ready selling-points image (keyed by instance id) so the
    // exported PNG matches the preview — mirrors the hero status gate.
    const readySp = (email.sellingPointsImages ?? []).filter(
      (img) => img.status === "ready" && img.url,
    );
    if (readySp.length) {
      const entries = await Promise.all(
        readySp.map(async (img) => {
          const dataUrl = await fetchImageDataUrl(img.url as string);
          return dataUrl ? ([img.instanceId, dataUrl] as const) : null;
        }),
      );
      const usable = entries.filter(
        (e): e is readonly [string, string] => !!e,
      );
      if (usable.length) setSellingPointsExportUrls(Object.fromEntries(usable));
    }
    // Embed each ready did-you-know image (W8), same contract as above.
    const readyDyk = (email.didYouKnowImages ?? []).filter(
      (img) => img.status === "ready" && img.url,
    );
    if (readyDyk.length) {
      const entries = await Promise.all(
        readyDyk.map(async (img) => {
          const dataUrl = await fetchImageDataUrl(img.url as string);
          return dataUrl ? ([img.instanceId, dataUrl] as const) : null;
        }),
      );
      const usable = entries.filter(
        (e): e is readonly [string, string] => !!e,
      );
      if (usable.length) setDidYouKnowExportUrls(Object.fromEntries(usable));
    }
    // Preset-template emails: embed each ready AI image slot (keyed by section
    // id) plus the raw real-photo URLs preset copy references, mirroring the
    // headless capture contract in buildEmailCaptureCtx.
    if (isPreset) {
      const readyPreset = (email.presetImages ?? []).filter(
        (img) => img.status === "ready" && img.url,
      );
      if (readyPreset.length) {
        const entries = await Promise.all(
          readyPreset.map(async (img) => {
            const dataUrl = await fetchImageDataUrl(img.url as string);
            return dataUrl ? ([img.instanceId, dataUrl] as const) : null;
          }),
        );
        const usable = entries.filter(
          (e): e is readonly [string, string] => !!e,
        );
        if (usable.length) setPresetExportUrls(Object.fromEntries(usable));
      }
      const rawUrls = new Set<string>();
      for (const inst of Object.values(blockCopy.instances ?? {})) {
        const preset = (inst as any)?.preset;
        for (const c of preset?.categories ?? []) {
          if (c?.imageUrl) rawUrls.add(c.imageUrl as string);
        }
      }
      const second = email.presetData?.secondProductImageUrl;
      if (second) rawUrls.add(second);
      if (rawUrls.size) {
        const entries = await Promise.all(
          Array.from(rawUrls).map(async (url) => {
            const dataUrl = await fetchImageDataUrl(url);
            return dataUrl ? ([url, dataUrl] as const) : null;
          }),
        );
        const usable = entries.filter(
          (e): e is readonly [string, string] => !!e,
        );
        if (usable.length) setPresetUrlExportUrls(Object.fromEntries(usable));
      }
    }
    // Let React repaint with chrome removed + the embedded images in place.
    await new Promise<void>((r) =>
      requestAnimationFrame(() => requestAnimationFrame(() => r())),
    );
    // Then WAIT for every <img> in the preview to be fully loaded AND decoded
    // before the caller captures. Setting the data-URL srcs above only schedules
    // a React re-render; the large hero AI-background image needs a decode pass
    // before its pixels actually exist, and the 2-rAF repaint is not enough — so
    // capturing immediately yields a flat/blank hero that then gets "polished"
    // into a flat hero (while the body, captured later in the same pass, looks
    // great). Each wait is capped so a broken/stuck image can never hang the
    // capture indefinitely.
    const container = previewRef.current;
    if (container) {
      const imgs = Array.from(container.querySelectorAll("img"));
      await Promise.all(
        imgs.map(
          (img) =>
            new Promise<void>((resolve) => {
              let settled = false;
              const finish = () => {
                if (settled) return;
                settled = true;
                resolve();
              };
              const decodeThenFinish = () => {
                if (typeof img.decode === "function") {
                  img.decode().then(finish, finish);
                } else {
                  finish();
                }
              };
              if (img.complete && img.naturalWidth > 0) {
                decodeThenFinish();
              } else {
                img.addEventListener("load", decodeThenFinish, { once: true });
                img.addEventListener("error", finish, { once: true });
              }
              // Safety cap: never block the whole capture on one stuck image.
              window.setTimeout(finish, 4000);
            }),
        ),
      );
    }
    // Fonts must also be fully loaded before the caller measures + captures: an
    // unloaded brand web font reflows text TALLER after measurement, which can
    // push the footer (unsubscribe / preferences / contact links) below the
    // captured canvas. Wait for them, then settle one more frame so the final,
    // footer-inclusive layout is what gets measured.
    if (typeof document !== "undefined" && (document as any).fonts?.ready) {
      try {
        await (document as any).fonts.ready;
      } catch {
        /* font loading is best-effort; never block capture on it */
      }
    }
    await new Promise<void>((r) =>
      requestAnimationFrame(() => requestAnimationFrame(() => r())),
    );
  };

  // Polished emails export from their canonical image blocks as TWO separate
  // artifacts (per the spec): a single full-length PNG and a ZIP of the
  // individual block PNGs (per-section CTA links live on the per-block images).
  // Load every polished block image as a data URL + decoded <img>. Data URLs
  // keep the stitch canvas untainted by cross-origin R2 URLs so it can export.
  const loadPolishedBlockImages = async () => {
    const blocks = email.polishBlocks ?? [];
    if (!blocks.length) throw new Error("No polished blocks to export");
    const loaded = await Promise.all(
      blocks.map(async (b) => {
        const dataUrl = (await fetchImageDataUrl(b.url)) || b.url;
        const img = await new Promise<HTMLImageElement | null>((res) => {
          const im = new Image();
          im.onload = () => res(im);
          im.onerror = () => res(null);
          im.src = dataUrl;
        });
        return { dataUrl, img };
      }),
    );
    const usable = loaded.filter(
      (x): x is { dataUrl: string; img: HTMLImageElement } => !!x.img,
    );
    if (!usable.length) throw new Error("Capture failed");
    return usable;
  };

  // Download the polished email as ONE stitched full-length PNG.
  const downloadPolishedFullPng = async () => {
    try {
      const usable = await loadPolishedBlockImages();
      const brandSlug = sanitizeFileName(brandName);
      const nameSlug = sanitizeFileName(name);
      const w = Math.max(...usable.map((x) => x.img.naturalWidth));
      const h = usable.reduce((s, x) => s + x.img.naturalHeight, 0);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const cx = canvas.getContext("2d");
      if (!cx) throw new Error("Canvas unavailable");
      cx.fillStyle = "#ffffff";
      cx.fillRect(0, 0, w, h);
      let y = 0;
      for (const x of usable) {
        cx.drawImage(x.img, 0, y, x.img.naturalWidth, x.img.naturalHeight);
        y += x.img.naturalHeight;
      }
      const fullBlob = await new Promise<Blob | null>((res) =>
        canvas.toBlob((bl) => res(bl), "image/png"),
      );
      if (!fullBlob) throw new Error("Capture failed");
      downloadBlob(fullBlob, `${brandSlug}-${nameSlug}-polished.png`);
      toast({ title: "Exported", description: "Full-length PNG downloaded." });
    } catch (e: any) {
      toast({
        title: "Export failed",
        description: e?.message || "Try again",
        variant: "destructive",
      });
    }
  };

  // Download a ZIP of the individual polished block PNGs (per-block links land
  // on the matching per-block image).
  const downloadPolishedBlocksZip = async () => {
    try {
      const usable = await loadPolishedBlockImages();
      const brandSlug = sanitizeFileName(brandName);
      const nameSlug = sanitizeFileName(name);
      const JSZip = (await import("jszip")).default;
      const zip = new JSZip();
      for (let i = 0; i < usable.length; i++) {
        const b = await (await fetch(usable[i].dataUrl)).blob();
        zip.file(`${String(i + 1).padStart(2, "0")}-block.png`, b);
      }
      const content = await zip.generateAsync({ type: "blob" });
      downloadBlob(content, `${brandSlug}-${nameSlug}-polished-blocks.zip`);
      toast({
        title: "Exported",
        description: `${usable.length} block image(s) zipped.`,
      });
    } catch (e: any) {
      toast({
        title: "Export failed",
        description: e?.message || "Try again",
        variant: "destructive",
      });
    }
  };

  // ---- Export images --------------------------------------------------------
  // Captures the on-screen preview blocks to PNG. Single-link mode → one combined
  // image; multi-link mode → one PNG per block, zipped. Editor chrome (hover
  // overlay + selection ring) is suppressed via the `isExporting` flag below.
  // A polished email exports from its canonical image blocks instead.
  const exportImages = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      // Embed the brand logo + hero photo as data URLs up-front so they render
      // in the capture (cross-origin/R2 images would otherwise be blank).
      await embedExportImages();

      const { toBlob } = await import("html-to-image");
      const captureOpts = CAPTURE_OPTS;

      const brandSlug = sanitizeFileName(brandName);
      const nameSlug = sanitizeFileName(name);

      if (normalizeEmailLinkConfig(linkConfig).mode === "cta") {
        const container = previewRef.current;
        if (!container) throw new Error("Preview not ready");
        const JSZip = (await import("jszip")).default;
        const zip = new JSZip();
        let added = 0;
        const exportBlocks = blocks.filter((k) => !hiddenBlocks.includes(k));
        for (let i = 0; i < exportBlocks.length; i++) {
          const key = exportBlocks[i];
          const node = container.querySelector<HTMLElement>(
            `[data-testid="preview-block-${key}"]`,
          );
          if (!node) continue;
          const blob = await toBlob(node, captureOpts);
          if (!blob) continue;
          const idx = String(i + 1).padStart(2, "0");
          const blockSlug = key.replace(/_/g, "-");
          zip.file(`${idx}-${blockSlug}.png`, blob);
          added++;
        }
        if (added === 0) throw new Error("Nothing to export");
        const content = await zip.generateAsync({ type: "blob" });
        downloadBlob(content, `${brandSlug}-${nameSlug}.zip`);
        toast({ title: "Exported", description: `${added} block image(s) zipped.` });
      } else {
        const node = previewRef.current;
        if (!node) throw new Error("Preview not ready");
        const blob = await toBlob(node, captureOpts);
        if (!blob) throw new Error("Capture failed");
        downloadBlob(blob, `${brandSlug}-${nameSlug}.png`);
        toast({ title: "Exported", description: "Combined image downloaded." });
      }
    } catch (e: any) {
      toast({
        title: "Export failed",
        description: e?.message || "Try again",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
      setLogoExportUrl(null);
      setHeroExportUrl(null);
      setUsVsThemExportUrl(null);
      setProductExportUrls([]);
      setProductImageExportUrls([]);
      setBlockImageExportUrls({});
    }
  };

  // The SINGLE polish entry point. Persists the live edits, captures the composed
  // (off-screen raw) email + protected-region map, uploads the original PNG, then
  // POSTs to claim a polish (202). The refetch/poll + loader take over from there.
  // Called by the auto-polish scheduler (generation + layout changes) and by the
  // send path — never directly by a copy text edit. With `awaitReady` it polls
  // the row until the server polish is ready/failed (used by send). With `silent`
  // it suppresses its own toast/error-state (the caller handles it, e.g. send's
  // MJML fallback).
  const runPolish = async (opts?: { awaitReady?: boolean; silent?: boolean }) => {
    setPolishLocal("running");
    setPolishLocalError(null);
    try {
      // Persist the live edits FIRST so the server fingerprints the same content
      // it will render from (its polishSourceHash must match the saved row, or the
      // editor would immediately read the polish as stale). The polish pipeline
      // (v11+) GENERATES the email from this saved structured data + the brand /
      // product photos server-side, so there is NOTHING to capture or upload from
      // the client — POST with no body.
      await saveQueue.flush();
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/email-express/emails/${email.id}/polish`,
      );
      const updated = await parseJsonResponse<EmailDetail>(res);
      queryClient.setQueryData(emailQueryKey, (prev: any) => ({
        ...(prev ?? {}),
        ...updated,
      }));
    } catch (e: any) {
      if (!opts?.silent) {
        setPolishLocalError(e?.message || "Polish failed");
        toast({
          title: "Polish failed",
          description: e?.message || "Try again",
          variant: "destructive",
        });
      }
      throw e;
    } finally {
      setPolishLocal("idle");
    }
    if (opts?.awaitReady) {
      const deadline = Date.now() + 5 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 2500));
        // A single poll tick that can't reach the server (a brief restart /
        // gateway blip returning the SPA's HTML, or a raw network error) must
        // NOT hard-fail an otherwise-healthy polish — ride through it and keep
        // waiting. A REAL failure (a 4xx/5xx from the GET) is NOT transient and
        // must surface promptly, preserving the existing failure semantics.
        let data: EmailDetail;
        try {
          const r = await apiRequest(
            "GET",
            `/api/brands/${brandId}/email-express/emails/${email.id}`,
          );
          data = await parseJsonResponse<EmailDetail>(r);
        } catch (e) {
          if (isTransientFetchError(e)) continue;
          throw e;
        }
        queryClient.setQueryData(emailQueryKey, data);
        if (data.polishStatus === "ready") return;
        if (data.polishStatus === "failed") {
          throw new Error(data.polishError || "Polishing failed");
        }
      }
      throw new Error("Polishing timed out");
    }
  };

  // Request a (re)polish after a LAYOUT change (add / remove / reorder / hide a
  // block). Shows the loader immediately (polishWanted) and debounces the runner
  // so a burst of layout edits coalesces into one polish (limits KIE billing).
  // Copy text edits NEVER call this.
  const requestLayoutPolish = () => {
    // Legacy preset emails never polish — the deterministic preview already
    // reflects any layout change (reorder / hide) live.
    if (isLegacyPreset) return;
    setPolishWanted(true);
    if (layoutDebounceRef.current) clearTimeout(layoutDebounceRef.current);
    layoutDebounceRef.current = setTimeout(
      () => setPolishKick((k) => k + 1),
      700,
    );
  };

  // ---- Partial (queued) re-polish ------------------------------------------
  // Re-render ONLY the named blocks (per-block Regenerate / Apply changes) or
  // pure RE-STITCH (reorder / remove / hide — no blockKeys, restitch:true). The
  // server reuses every other block's persisted image + the stable Stage-1
  // design spec, so this is far cheaper than a full re-polish. Saves the live
  // edits FIRST so the server fingerprints (and stamps the per-block contentHash
  // on) the same content the editor hashed — otherwise the regenerated block
  // would read as perpetually pending. Legacy rows (chunks without a blockKey)
  // transparently fall back to a full re-polish on the server.
  const partialPolishMutation = useMutation({
    mutationFn: async (vars: {
      blockKeys?: string[];
      restitch?: boolean;
      feedback?: string;
      useCurrentHeroImage?: boolean;
    }) => {
      await saveQueue.flush();
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/email-express/emails/${email.id}/polish/blocks`,
        vars,
      );
      return await parseJsonResponse<EmailDetail>(res);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(emailQueryKey, (prev: any) => ({
        ...(prev ?? {}),
        ...updated,
      }));
    },
    onError: (e: any) => {
      setRegeneratingKeys([]);
      toast({
        title: "Couldn't regenerate",
        description: e?.message || "Try again",
        variant: "destructive",
      });
    },
    // Drop the optimistic loader flag once the round-trip settles; from here the
    // server status (`pending` => !polished) keeps the whole-email loader up
    // until the run finishes (or, on error/no-op, the stack returns).
    onSettled: () => {
      setPartialPending(false);
    },
  });

  // Regenerate a specific set of edited blocks (queued). Records the in-flight
  // keys (to gate their edit-panel button) and flips the optimistic loader flag
  // so the whole-email loader shows immediately.
  const regenerateBlocks = (
    keys: string[],
    feedback?: string,
    opts?: { useCurrentHeroImage?: boolean },
  ) => {
    if (!keys.length) return;
    setRegeneratingKeys(keys);
    setPartialPending(true);
    partialPolishMutation.mutate({
      blockKeys: keys,
      ...(feedback?.trim() ? { feedback: feedback.trim() } : {}),
      ...(opts?.useCurrentHeroImage ? { useCurrentHeroImage: true } : {}),
    });
  };

  // Task #2329 — hero swap panel state, derived from the SERVER-persisted
  // heroSwapPending flag (survives reloads; failed applies keep it set) plus
  // the live mutation/polish busy signals.
  const heroSwapState = heroSwapPanelState({
    heroSwapPending: email.heroSwapPending,
    editMode,
    polishPending: partialPolishMutation.isPending || polishBusy,
    actionsBusy: heroImageBusy,
  });

  // Pure re-stitch after a structural change (reorder / remove / hide): no block
  // re-renders, the carried images are just re-stacked + seam-checked.
  const restitchLayout = () => {
    setRegeneratingKeys([]);
    setPartialPending(true);
    partialPolishMutation.mutate({ restitch: true });
  };

  // A structural (layout) change. When a per-block manifest exists we re-stitch
  // (cheap); otherwise (initial / legacy) fall back to the debounced full polish
  // so first-generation behavior is preserved.
  const onLayoutStructureChanged = () => {
    if (polished && (email.polishBlocks ?? []).some((b) => !!b.blockKey)) {
      restitchLayout();
    } else {
      requestLayoutPolish();
    }
  };

  // Clear the session regeneration markers once a run settles (ready/failed).
  useEffect(() => {
    if (!polishing) setRegeneratingKeys([]);
  }, [polishing]);

  // Retry a failed polish from the failed card: clear the error + the once-per-
  // hash auto guard, then fire the runner.
  const retryPolish = () => {
    setPolishLocalError(null);
    autoPolishHashRef.current = null;
    setPolishWanted(true);
    setPolishKick((k) => k + 1);
  };

  // ---- Block helpers --------------------------------------------------------
  // `blocks` holds block INSTANCE ids (a bare type, or "<type>__<suffix>" for a
  // repeated optional block). Locked types (hero/body/footer) are always bare.
  const optionalIncluded = presetDef
    ? // Preset emails: the reorderable set is the template's UNLOCKED sections
      // in their current normalized order (locked sections stay pinned).
      normalizeEmailPresetBlocks(presetDef, blocks).filter(
        (k) => !presetLockedKeys.has(k),
      )
    : blocks.filter((k) => !LOCKED_KEYS.includes(emailBlockType(k)));
  // Blocks the user ADDED to a block-driven email (beyond the template's own
  // fixed sequence) can be deleted again; template-own blocks can only hide.
  const blockDrivenTemplateOwn = useMemo(
    () =>
      new Set<string>(
        isBlockDriven
          ? getEmailPresetTemplate(email.presetTemplateKey)?.blockDrivenBlocks ?? []
          : [],
      ),
    [isBlockDriven, email.presetTemplateKey],
  );
  const canDeleteBlock = (key: string) =>
    !isPreset || (isBlockDriven && !blockDrivenTemplateOwn.has(key));

  // Display labels per instance: append an ordinal ("Reviews 2") only when a
  // type appears more than once, so duplicates are distinguishable everywhere.
  const instanceLabels = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const id of blocks) {
      const t = emailBlockType(id);
      totals[t] = (totals[t] || 0) + 1;
    }
    const running: Record<string, number> = {};
    const out: Record<string, string> = {};
    for (const id of blocks) {
      const t = emailBlockType(id);
      const base =
        BLOCK_META[t]?.label ||
        (isPreset ? emailPresetSectionLabel(email.presetTemplateKey, t) : t);
      running[t] = (running[t] || 0) + 1;
      out[id] = totals[t] > 1 ? `${base} ${running[t]}` : base;
    }
    return out;
  }, [blocks]);
  const labelFor = (id: string) =>
    instanceLabels[id] ||
    BLOCK_META[emailBlockType(id)]?.label ||
    (isPreset
      ? emailPresetSectionLabel(email.presetTemplateKey, emailBlockType(id))
      : id);

  // Selecting a block opens the right-hand edit panel. The left (Blocks)
  // sidebar STAYS open — auto-collapsing it on narrow viewports read as "the
  // sidebar closed on me" (user feedback Jul 29 2026); the preview zooms to
  // fit whatever width remains, and users can still collapse it manually.
  const selectBlock = (id: string) => {
    setSelectedBlock(id);
  };

  function setOptional(nextOptional: string[]) {
    if (presetDef) {
      // Rebuild the preset order: locked sections at their template positions,
      // unlocked slots filled in the given order.
      setBlocks(normalizeEmailPresetBlocks(presetDef, nextOptional));
      return;
    }
    if (isBlockDriven) {
      // Perfect Pair layout order is FIXED — mandatory blocks are always in,
      // optional blocks snap to their template-defined slots.
      setBlocks(normalizeEmailBlockDrivenBlocksPreserving(nextOptional));
      return;
    }
    setBlocks(["hero", "body", ...nextOptional, "footer"]);
  }
  // Add a NEW image block instance (Task #2286 follow-through): image blocks
  // carry no AI copy (image_only is photo-only; image_discount falls back to
  // the email's sale settings), so adding one is a pure layout change — no
  // copy worker, no auto-polish. On polished per-block emails the missing
  // manifest entry flags it in the queued-changes banner; the user applies
  // explicitly. Fixed-section preset templates can't gain blocks; block-driven
  // ("Perfect Pair") emails add repeated instances as "<type>__<n>" ids (the
  // preserving normalizer ranks them by base type at the template slot).
  function addImageBlock(type: "image_only" | "image_discount") {
    let id: string = type;
    if (blocks.includes(id)) {
      let n = 2;
      while (blocks.includes(`${type}__${n}`)) n++;
      id = `${type}__${n}`;
    }
    setOptional([...optionalIncluded, id]);
    setSelectedBlock(id);
  }
  function removeBlock(key: string) {
    if (isBlockDriven) {
      // Filter the FULL list directly so the user's custom order survives.
      setBlocks(
        normalizeEmailBlockDrivenBlocksPreserving(
          blocks.filter((k) => k !== key),
        ),
      );
    } else {
      setOptional(optionalIncluded.filter((k) => k !== key));
    }
    setHiddenBlocks((prev) => prev.filter((k) => k !== key));
    if (selectedBlock === key) setSelectedBlock("hero");
    // A block that never made it into the polished composite (no manifest
    // entry — e.g. just added, then deleted before it was ever rendered) isn't
    // in the stitched image, so removing it must NOT kick a re-stitch/polish.
    // The pending-changes banner simply disappears with the block.
    if (polished && hasBlockManifest && !manifestByKey.has(key)) return;
    onLayoutStructureChanged();
  }
  function toggleHidden(key: string) {
    setHiddenBlocks((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
    // Same rule as removeBlock: hiding/unhiding a block that isn't in the
    // polished composite changes nothing in the stitched image — no re-stitch.
    if (polished && hasBlockManifest && !manifestByKey.has(key)) return;
    onLayoutStructureChanged();
  }
  const isHidden = (key: string) => hiddenBlocks.includes(key);
  const visibleBlocks = presetDef
    ? // Preset render order: locked sections pinned, stored order honored for
      // unlocked ones, hidden honored (locked sections never hide).
      visibleEmailPresetBlocks(presetDef, blocks, hiddenBlocks)
    : isBlockDriven
      ? // Perfect Pair: fixed template order, defensively re-derived (unknown
        // keys drop, mandatory hero/body/footer always render), hidden honored.
        normalizeEmailBlockDrivenBlocksPreserving(blocks).filter(
          (k) => !hiddenBlocks.includes(k),
        )
      : blocks.filter((k) => !hiddenBlocks.includes(k));
  // Link-config helpers: legacy "multi" configs read as per-CTA ("cta"), and
  // the per-CTA fields only cover blocks that render a real button (hero/body).
  // Per-CTA link override for a block (Task #1914). Only meaningful when the
  // stored config is in per-CTA mode (legacy "multi" normalizes to "cta");
  // legacy "single" drafts have no active overrides even if stale blockLinks
  // linger from an old mode switch.
  const ctaLinkOverrideFor = (key: string): string => {
    const cfg = normalizeEmailLinkConfig(linkConfig);
    if (cfg.mode !== "cta") return "";
    return cfg.blockLinks[key] || cfg.blockLinks[emailBlockType(key)] || "";
  };
  // Set (url) or clear (null/"") a per-block CTA link override. Setting the
  // FIRST override on a "single"-mode draft flips it to "cta" mode and drops
  // any stale legacy blockLinks so only the new override activates.
  const setCtaLinkOverride = (key: string, url: string | null) => {
    setLinkConfig((p) => {
      const cfg = normalizeEmailLinkConfig(p);
      const trimmed = (url ?? "").trim();
      if (trimmed) {
        const base = cfg.mode === "cta" ? { ...(p.blockLinks || {}) } : {};
        return { ...p, mode: "cta", blockLinks: { ...base, [key]: trimmed } };
      }
      const next = { ...(p.blockLinks || {}) };
      delete next[key];
      delete next[emailBlockType(key)];
      return { ...p, blockLinks: next };
    });
  };
  function reorderTo(targetKey: string) {
    if (!dragKey || dragKey === targetKey) return;
    if (isBlockDriven) {
      // Block-driven emails reorder over the FULL block list (optionals can
      // move around hero/body, e.g. an image band above the body copy). The
      // footer stays pinned last via the preserving normalizer.
      const from = blocks.indexOf(dragKey);
      const to = blocks.indexOf(targetKey);
      if (from < 0 || to < 0) return;
      const next = [...blocks];
      next.splice(from, 1);
      next.splice(to, 0, dragKey);
      setBlocks(normalizeEmailBlockDrivenBlocksPreserving(next));
      setDragKey(null);
      if (!(polished && (email.polishBlocks ?? []).some((b) => !!b.blockKey))) {
        onLayoutStructureChanged();
      }
      return;
    }
    const from = optionalIncluded.indexOf(dragKey);
    const to = optionalIncluded.indexOf(targetKey);
    if (from < 0 || to < 0) return;
    const next = [...optionalIncluded];
    next.splice(from, 1);
    next.splice(to, 0, dragKey);
    setOptional(next);
    setDragKey(null);
    // On an already-polished per-block email a reorder is QUEUED (the stale
    // banner + "Apply changes" surface it), NOT applied immediately — so a
    // sequence of drags doesn't interrupt each drop with the whole-email
    // loader. The debounced autosave persists the new order; the next partial
    // run / re-stitch stacks in the live order. Pre-manifest/legacy emails
    // keep the immediate path (debounced full polish), as before.
    if (!(polished && (email.polishBlocks ?? []).some((b) => !!b.blockKey))) {
      onLayoutStructureChanged();
    }
  }

  // ---- Auto-polish scheduler ------------------------------------------------
  // Everything needed for a faithful capture is present: copy finished, no block
  // is still being written, and no image for a VISIBLE block is still rendering.
  // (A failed image doesn't block — we never want to wait forever.)
  const anyBlockCopyPending = Object.values(
    (email.blockCopyStatus ?? {}) as Record<string, string>,
  ).some((s) => s === "pending");
  const anyVisibleImagePending =
    email.heroImageStatus === "pending" ||
    (visibleBlocks.some((k) => emailBlockType(k) === "us_vs_them") &&
      email.usVsThemImageStatus === "pending") ||
    (visibleBlocks.some((k) => emailBlockType(k) === "comparison_table") &&
      email.comparisonTableImageStatus === "pending") ||
    (email.sellingPointsImages ?? []).some(
      (img) =>
        img.status === "pending" && visibleBlocks.includes(img.instanceId),
    ) ||
    (email.didYouKnowImages ?? []).some(
      (img) =>
        img.status === "pending" && visibleBlocks.includes(img.instanceId),
    );
  const allContentReady =
    email.copyStatus !== "pending" &&
    !anyBlockCopyPending &&
    !anyVisibleImagePending;

  // Detect an image regen finishing (pending → not-pending). The regenerated
  // image lands on the SAME R2 URL, so the source hash can't see it — flag it
  // as a queued change so the banner offers "Update preview" instead of
  // silently disappearing with a stale captured stack on screen.
  useEffect(() => {
    if (anyVisibleImagePending) {
      prevImagePendingRef.current = true;
    } else if (prevImagePendingRef.current) {
      prevImagePendingRef.current = false;
      if (autoCaptureAfterRegenRef.current) {
        // This regen was already approved via "Update preview" — render the
        // fresh image into the captured preview automatically.
        autoCaptureAfterRegenRef.current = false;
        runPreviewCapture();
      } else {
        setImageRefreshQueued(true);
      }
    }
  }, [anyVisibleImagePending]);

  // Polish disabled: capture each visible block as a retina PNG (uploaded to
  // R2 + persisted on the row — the exact images the send embeds), so the
  // canonical preview is always the sendable image stack. The FIRST capture
  // (nothing persisted yet) runs automatically; after that, content changes
  // QUEUE (amber banner, like V1's polish "Apply changes") and only re-capture
  // when the user clicks "Update preview" — or on send, which always captures
  // fresh. A failure falls back to the raw HTML preview.
  const runPreviewCapture = () => {
    if (captureBusyRef.current) return;
    const hash = computeEmailPolishSourceHash(email as any);
    lastCaptureHashRef.current = hash;
    captureBusyRef.current = true;
    setCapturing(true);
    (async () => {
      // Captures are occasionally flaky (transient font/image fetch failures
      // inside html-to-image) — retry a couple of times before surfacing a
      // failure, matching the resilience the old auto-recapture loop had.
      let lastErr: unknown = null;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const blocks = await captureAndPersistEmailBlocks({
            brandId,
            email,
            brand,
          });
          setLocalCaptured(blocks);
          setCaptureFailed(false);
          setImageRefreshQueued(false);
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
          console.warn(
            `[email-editor] preview block capture failed (attempt ${attempt}/3)`,
            e,
          );
          await new Promise((r) => setTimeout(r, 800 * attempt));
        }
      }
      if (lastErr !== null) {
        lastCaptureHashRef.current = null;
        setCaptureFailed(true);
        toast({
          title: "Preview update failed",
          description: "Couldn't refresh the preview images — try again.",
          variant: "destructive",
        });
      }
      captureBusyRef.current = false;
      setCapturing(false);
    })();
  };
  // "Update preview" = the user's approval step. If image regens are queued,
  // fire them now (the banner shows "regenerating"; once the fresh image lands
  // the capture runs automatically — the approval already happened). Otherwise
  // just re-capture the current content.
  const approveQueuedAndUpdatePreview = () => {
    const pending = queuedSectionRegens.filter((q) => blocks.includes(q.block));
    if (pending.length > 0) {
      autoCaptureAfterRegenRef.current = true;
      setQueuedSectionRegens([]);
      for (const q of pending) {
        regenMutation.mutate({
          block: q.block,
          mode: "section",
          scope: q.scope,
          feedback: q.feedback,
          background: true,
        });
      }
      return;
    }
    runPreviewCapture();
  };
  // Auto-capture ONLY when no captured preview exists yet (first open after
  // generation, or every prior capture failed) — later changes wait for the
  // user's explicit "Update preview".
  const hasAnyCaptured =
    (localCaptured?.length ?? 0) > 0 ||
    (((email as any).capturedBlocks as EmailCapturedBlock[] | null)?.length ??
      0) > 0;
  useEffect(() => {
    if (emailAutoPolishEnabled || isLegacyPreset) return;
    if (polished || editMode) return;
    if (!allContentReady) return;
    if (captureBusyRef.current) return;
    if (isExporting || sendMutation.isPending || regenMutation.isPending) return;
    if (hasAnyCaptured) return;
    const hash = computeEmailPolishSourceHash(email as any);
    if (lastCaptureHashRef.current === hash) return;
    runPreviewCapture();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allContentReady, polished, editMode, email, isExporting, sendMutation.isPending, regenMutation.isPending, hasAnyCaptured, emailAutoPolishEnabled]);

  // (A) Ensure a polish EXISTS for ready content, exactly once per content hash.
  // Fires after generation completes (or when an unpolished email is opened).
  // Never auto-retries a failure (the user retries from the failed card), and
  // never re-fires for copy edits once a polish exists (polished === true).
  useEffect(() => {
    if (!emailAutoPolishEnabled) return; // polish disabled — live preview is canonical
    if (isLegacyPreset) return; // legacy preset emails never enter the polish pipeline
    if (polished || polishing || polishErrored) return;
    if (polishLocal !== "idle" || polishWanted) return;
    if (!allContentReady) return;
    const hash = computeEmailPolishSourceHash(email as any);
    if (autoPolishHashRef.current === hash) return;
    autoPolishHashRef.current = hash;
    setPolishWanted(true);
    setPolishKick((k) => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allContentReady, polished, polishing, polishErrored, polishLocal, polishWanted, emailAutoPolishEnabled]);

  // (B) The runner: when a polish is wanted, the content is ready, and nothing
  // else is in flight, run it once. A layout change debounces a kick into here;
  // an AI-copy block add waits here until its new copy lands (allContentReady).
  useEffect(() => {
    if (!emailAutoPolishEnabled) return; // polish disabled — live preview is canonical
    if (isLegacyPreset) return; // legacy preset emails never enter the polish pipeline
    if (!polishWanted || polishKick === 0) return;
    if (polishLocal !== "idle" || polishing) return;
    if (!allContentReady) return;
    if (regenMutation.isPending || isExporting || sendMutation.isPending) return;
    setPolishWanted(false);
    void runPolish().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    polishKick,
    polishWanted,
    polishLocal,
    polishing,
    allContentReady,
    regenMutation.isPending,
    isExporting,
    sendMutation.isPending,
    emailAutoPolishEnabled,
  ]);

  function patchBlockCopy(patch: Partial<EmailBlockCopy>) {
    setBlockCopy((prev) => ({ ...prev, ...patch }));
  }
  // Write per-instance copy, seeding from the resolved (legacy-aware) copy so a
  // bare instance's first edit doesn't drop pre-existing legacy fields.
  function patchInstanceCopy(id: string, patch: Partial<EmailInstanceCopy>) {
    setBlockCopy((prev) => ({
      ...prev,
      instances: {
        ...(prev.instances || {}),
        [id]: { ...resolveEmailInstanceCopy(prev, id), ...patch },
      },
    }));
  }
  // Task #2556: patch a single slot's image treatment on an eligible block
  // instance. Only the treatment map is updated; copy fields are untouched.
  function patchImageTreatment(id: string, slot: string, treatment: EmailImageTreatment) {
    setBlockCopy((prev) => {
      const existing = resolveEmailInstanceCopy(prev, id);
      const prevTreatments = existing.imageTreatments ?? {};
      // Keep an explicit automatic entry. Its hash remains byte-compatible with
      // absence, while the worker can distinguish a deliberate restoration from
      // an untouched legacy default and preserve the old block on apply failure.
      const nextTreatments = { ...prevTreatments, [slot]: treatment };
      return {
        ...prev,
        instances: {
          ...(prev.instances || {}),
          [id]: {
            ...existing,
            imageTreatments: nextTreatments,
          },
        },
      };
    });
  }
  // Per-instance section punchline (punchlines keyed by instance id; legacy
  // type-keyed entries equal a bare instance id, so they stay readable).
  function setInstancePunchline(id: string, val: string) {
    setBlockCopy((prev) => ({
      ...prev,
      punchlines: { ...(prev.punchlines || {}), [id]: val },
    }));
  }

  // ---- Derived context ------------------------------------------------------
  const tag =
    email.type === "flow"
      ? FLOW_LABELS[email.flowType || ""] || "Flow"
      : email.narrativeAngleLabel || "—";
  const saleText = email.saleDiscountLabel
    ? [
        email.saleDiscountLabel,
        email.salePercentage ? `${email.salePercentage}% off` : null,
        email.saleCode ? `Code ${email.saleCode}` : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "No sale";

  // ---- Per-block polish state (queued partial re-render) --------------------
  // Fingerprint the LIVE local content (copy + layout + product/sale anchors)
  // exactly as the server does, so an edited block is flagged Pending the moment
  // the field changes — before the debounced autosave lands.
  const blockHashInput = useMemo(
    () => ({
      headline,
      bodyCopy,
      ctaText,
      blockCopy,
      linkConfig,
      footerOverrides,
      saleDiscountLabel: email.saleDiscountLabel,
      salePercentage: email.salePercentage,
      saleCode: email.saleCode,
      productImageUrls: email.productImageUrls,
      productListImages: email.productListImages,
    }),
    [
      headline,
      bodyCopy,
      ctaText,
      blockCopy,
      linkConfig,
      footerOverrides,
      email.saleDiscountLabel,
      email.salePercentage,
      email.saleCode,
      email.productImageUrls,
      email.productListImages,
    ],
  );
  // Manifest entry per visible block. New entries carry their own blockKey; for a
  // legacy manifest (no keys) fall back to positional mapping (stack order ==
  // visible order) so the old stack still renders, just without per-block edits.
  const manifestByKey = useMemo(() => {
    const m = new Map<string, NonNullable<typeof email.polishBlocks>[number]>();
    (email.polishBlocks ?? []).forEach((b, i) => {
      const k = b.blockKey ?? visibleBlocks[i];
      if (k) m.set(k, b);
    });
    return m;
  }, [email.polishBlocks, visibleBlocks]);
  // Partial re-rendering is only possible when EVERY block in the manifest
  // carries a blockKey; otherwise the email predates the marker fields and edits
  // route through a full re-polish.
  const hasBlockManifest =
    (email.polishBlocks?.length ?? 0) > 0 &&
    (email.polishBlocks ?? []).every((b) => !!b.blockKey);

  const bodyImagePreference: EmailBodyImagePreference =
    resolveEmailBodyImagePreference(
      resolveEmailInstanceCopy(blockCopy, "body"),
    );
  const bodyManifest = resolveEmailBodyPolishBlock(
    email.polishBlocks ?? [],
    visibleBlocks,
  );
  const bodyVisualHash = computeEmailPolishBlockVisualHash(
    {
      productImageUrls: email.productImageUrls,
      productListImages: email.productListImages,
      blockCopy,
    },
    "body",
  );
  // Legacy automatic Body Copy manifests predate visualHash; those remain
  // quietly up to date. Any explicit treatment on such a row is necessarily a
  // new staged choice. Once a Body section has been rendered, visualHash gives
  // exact selected/none/automatic transition tracking independent of copy edits.
  const bodyImageNeedsApply = emailBodyImageNeedsApply({
    hasManifest: !!bodyManifest,
    manifestVisualHash: bodyManifest?.visualHash,
    currentVisualHash: bodyVisualHash,
    preference: bodyImagePreference,
  });
  const bodyImageApplyBusy =
    regeneratingKeys.includes("body") &&
    (partialPolishMutation.isPending || partialPending || polishing);
  const capturedBody = capturedList.find((block) => block.blockKey === "body");
  const shouldInspectLegacyBodyImage =
    selectedBlock === "body" &&
    !editMode &&
    polished &&
    !!bodyManifest?.url &&
    bodyManifest.hasImagery === undefined;
  const legacyBodyImageAvailabilityPath =
    `/api/brands/${brandId}/email-express/emails/${email.id}` +
    "/body-image-availability";
  const { data: legacyBodyImageAvailability, isFetching: checkingLegacyBodyImage } =
    useQuery<{ hasImagery: boolean }>({
      queryKey: [legacyBodyImageAvailabilityPath, bodyManifest?.url],
      enabled: shouldInspectLegacyBodyImage,
      staleTime: Infinity,
      retry: false,
      queryFn: async () => {
        const response = await fetch(legacyBodyImageAvailabilityPath, {
          credentials: "include",
        });
        if (!response.ok) {
          throw new Error(
            `Body Copy image inspection failed (${response.status})`,
          );
        }
        return (await response.json()) as { hasImagery: boolean };
      },
    });
  // Replacement is available only when the Body currently on screen is known
  // to contain imagery. Never infer this from available product photos. Missing
  // legacy metadata is inspected from the current saved pixels on demand; an
  // explicit false marker still fails closed.
  const bodyHasRenderedImage = emailBodyHasReplaceableImage({
    editMode,
    polished,
    preference: bodyImagePreference,
    manifestHasImagery: bodyManifest?.hasImagery,
    capturedHasImagery: capturedBody?.hasImagery,
    legacyDetectedHasImagery: legacyBodyImageAvailability?.hasImagery,
  });

  // Never start a polish/restitch merely because an existing email was opened.
  // Legacy delivery assets are normalized by the send path, while deliberate
  // structural edits still call restitchLayout() through
  // onLayoutStructureChanged. Opening Edit must remain read-only.
  // Blocks whose live content no longer matches their rendered image. Footer is
  // excluded (its panel is read-only here). A manifest entry with no contentHash
  // is a legacy render — don't over-flag it. A visible block with no entry at all
  // (freshly added) is Pending.
  const pendingBlockKeys = useMemo(() => {
    if (!polished || !hasBlockManifest) return [] as string[];
    return visibleBlocks.filter((key) => {
      if (key === "footer") return false;
      const entry = manifestByKey.get(key);
      if (!entry) return true;
      if (!entry.contentHash) return false;
      return entry.contentHash !== computeEmailPolishBlockHash(blockHashInput, key);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polished, hasBlockManifest, visibleBlocks, manifestByKey, blockHashInput]);
  const pendingSet = useMemo(() => new Set(pendingBlockKeys), [pendingBlockKeys]);
  // A pure reorder changes no block's content, so it can never surface through
  // pendingBlockKeys — detect it by comparing the manifest's stack order with
  // the live visible order over the blocks present in both (add/remove/hide
  // differences are filtered out; those run their own immediate re-stitch).
  const layoutOrderStale = useMemo(() => {
    if (!polished || !hasBlockManifest) return false;
    // Compare against the order the polish renderer would ACTUALLY stack
    // (shared helper: hero/body first, footer last, comparison-family blocks
    // separated) — NOT the raw stored order. The renderer legitimately
    // re-derives the order (e.g. sliding a block between two adjacent
    // comparison sections), so comparing raw order false-flags "block order
    // changed" forever on such emails even though a re-render would produce
    // the identical stack.
    // Block-driven template emails render in the template's fixed order
    // (visibleBlocks already IS that order via visibleBlocksOf) — the
    // hero/body-first scaffold would false-flag e.g. a leading logo_banner.
    const live = (
      isBlockDriven ? visibleBlocks : orderedEmailRenderKeys(visibleBlocks)
    ).filter((k) => manifestByKey.has(k));
    const liveSet = new Set(live);
    const manifest = (email.polishBlocks ?? [])
      .map((b) => b.blockKey)
      .filter((k): k is string => !!k && liveSet.has(k));
    return manifest.join("\u0000") !== live.join("\u0000");
  }, [polished, hasBlockManifest, visibleBlocks, manifestByKey, email.polishBlocks, isBlockDriven]);
  // Everything currently queued (edited sections + a pending reorder counts as
  // one change). Applying re-renders the edited blocks — the partial run
  // re-stacks ALL blocks in the live order, so a queued reorder rides along for
  // free — or, when ONLY the order changed, runs a pure (free) re-stitch.
  const queuedChangeCount =
    pendingBlockKeys.length + (layoutOrderStale ? 1 : 0);
  const applyQueuedChanges = () => {
    if (pendingBlockKeys.length > 0) {
      regenerateBlocks(pendingBlockKeys);
    } else if (layoutOrderStale) {
      restitchLayout();
    }
  };
  // The targeted block(s) for the in-flight partial run. This no longer drives a
  // per-block PREVIEW spinner — the whole-email loader covers the preview for any
  // regenerate / update. It only marks the block's edit-panel button as a
  // disabled "Regenerating…" so a second click can't double-fire the run.
  const regeneratingSet = useMemo(
    () => new Set(regeneratingKeys),
    [regeneratingKeys],
  );
  const blockEditState = (key: string): BlockEditState =>
    regeneratingSet.has(key)
      ? "regenerating"
      : manifestByKey.get(key)?.renderError
        ? "failed"
        : pendingSet.has(key)
          ? "pending"
          : "polished";
  // Run `action` immediately UNLESS the user has un-applied queued changes
  // (per-block pipeline only): edited sections and/or a queued reorder — then
  // warn first so they don't export/send a polished image that's stale. Stores
  // the action to resume if they choose "Continue anyway".
  const guardPendingThen = (action: () => void) => {
    if (hasBlockManifest && (pendingBlockKeys.length > 0 || layoutOrderStale)) {
      setPendingActionGuard(() => action);
    } else {
      action();
    }
  };

  // ---- Task #2556: per-slot image treatment for the selected eligible block --
  // Computed only when the selected block is an eligible type AND its current
  // layout variant is image-bearing. Fails closed (undefined) for image-free
  // layouts, unsupported block types, and — critically — any section without a
  // KEYED block manifest entry: generic Apply targets partial polish, which
  // must be able to re-render THIS exact block. Ambiguous legacy / captured-only
  // sections that cannot safely target the exact block never show controls.
  const blockBodyImageProp: EmailBlockEditPanelProps["blockBodyImage"] = (() => {
    if (!selectedBlock) return undefined;
    const selType = emailBlockType(selectedBlock);
    if (!isEmailImageTreatmentEligible(selType)) return undefined;
    const selInstance = resolveEmailInstanceCopy(blockCopy, selectedBlock);
    const selVariant = resolveEmailLayoutVariant(selType, selInstance);
    const variantHasImage = emailVariantNeedsImage(selType, selVariant);
    if (!variantHasImage) return undefined;

    // Fail closed unless a keyed block manifest exists for THIS block. Without a
    // manifest chunk we can't run a targeted partial polish, so we must not
    // surface controls on ambiguous legacy/captured-only sections.
    const blockManifest = manifestByKey.get(selectedBlock);
    if (!hasBlockManifest || !blockManifest) return undefined;

    // Build the slots list. For product_list, one slot per visible tile.
    // For all other eligible types, a single "main" slot.
    type Slot = NonNullable<EmailBlockEditPanelProps["blockBodyImage"]>["slots"][number];
    const slots: Slot[] = [];

    if (selType === "product_list") {
      // Determine which images are available (bundle or range). Product List
      // tiles always render a real product photo — image-free tiles are NOT
      // supported (the renderer can't omit a tile's photo), so allowRemove is
      // false for every tile. Choose / upload / restore still work.
      const bundleImages = email.productListImages ?? [];
      const rangeImages = (email.productImageUrls ?? []).map((url) => ({
        name: email.name ?? "",
        imageUrl: url,
      }));
      const plType = selInstance.productListType ?? (bundleImages.length ? "bundle" : "range");
      const tileImages = plType === "bundle" ? bundleImages : rangeImages;
      tileImages.forEach((img, i) => {
        const slot = productListTileSlot(i);
        const treatment = resolveEmailProductListImageTreatment(selInstance, slot);
        slots.push({
          slot,
          label: `Tile ${i + 1} image (${img.name || `Product ${i + 1}`})`,
          treatment,
          hasImage: true, // product_list always shows real photos
          currentUrl: img.imageUrl,
          allowRemove: false,
        });
      });
    } else {
      // Singular blocks: one "main" slot. Their renderers support omitting the
      // supporting image, so removal (mode:"none") is allowed.
      const treatment = resolveEmailImageTreatment(selInstance, EMAIL_IMAGE_TREATMENT_MAIN_SLOT);
      let currentUrl = "";
      let hasImage = false;
      if (selType === "selling_points") {
        const spImg = (email.sellingPointsImages ?? []).find(
          (img) => img.instanceId === selectedBlock,
        );
        currentUrl = spImg?.url ?? "";
        hasImage = spImg?.status === "ready" && !!currentUrl;
      } else if (selType === "did_you_know") {
        const dykImg = (email.didYouKnowImages ?? []).find(
          (img) => img.instanceId === selectedBlock,
        );
        currentUrl = dykImg?.url ?? "";
        hasImage = dykImg?.status === "ready" && !!currentUrl;
      } else if (selType === "us_vs_them") {
        currentUrl = email.usVsThemImageUrl ?? "";
        hasImage = email.usVsThemImageStatus === "ready" && !!currentUrl;
      } else if (selType === "comparison_table") {
        currentUrl = email.comparisonTableImageUrl ?? "";
        hasImage = email.comparisonTableImageStatus === "ready" && !!currentUrl;
      }
      // Only expose the picker when we have a verified image to replace, OR the
      // user has already staged a non-automatic treatment (so restore stays
      // reachable). Fail closed only for pristine automatic + no rendered image.
      if (!hasImage && treatment.mode === "automatic") return undefined;
      slots.push({
        slot: EMAIL_IMAGE_TREATMENT_MAIN_SLOT,
        label: "Image",
        treatment,
        hasImage: hasImage || treatment.mode !== "automatic",
        currentUrl,
        allowRemove: true,
      });
    }

    if (slots.length === 0) return undefined;

    // needsApply: compare the manifest's stamped visualHash against the block's
    // CURRENT visual hash for EVERY transition (selected → none → automatic and
    // back). This tracks image-treatment changes independent of copy edits and
    // fires even when restoring to automatic (which lowers the current hash back
    // to the legacy/automatic value — a change the manifest must re-render).
    // Legacy manifests predate visualHash: those are "pending" ONLY when the
    // current treatment is non-automatic (a genuinely new staged choice); a
    // legacy automatic chunk is quietly up to date. Never compare contentHash.
    const currentVisualHash = computeEmailPolishBlockVisualHash(
      {
        productImageUrls: email.productImageUrls,
        productListImages: email.productListImages,
        blockCopy,
      },
      selectedBlock,
    );
    const anyNonAutomatic = slots.some((s) => s.treatment.mode !== "automatic");
    const needsApply = blockManifest.visualHash
      ? blockManifest.visualHash !== currentVisualHash
      : anyNonAutomatic;
    const applyBusy =
      regeneratingKeys.includes(selectedBlock) &&
      (partialPolishMutation.isPending || partialPending || polishing);

    // Deduped photo list: BOTH product-photo collections the picker offers —
    // the primary-product photos AND the bundle product_list member photos —
    // some of which may be externally hosted.
    const photos = Array.from(
      new Set([
        ...(email.productImageUrls ?? []),
        ...((email.productListImages ?? [])
          .map((p) => p?.imageUrl)
          .filter((u): u is string => typeof u === "string" && !!u)),
      ]),
    );

    return {
      slots,
      photos,
      busy: blockImageUploadMutation.isPending &&
        blockImageUploadMutation.variables?.blockKey === selectedBlock,
      uploadingSlot: blockImageUploadMutation.isPending &&
        blockImageUploadMutation.variables?.blockKey === selectedBlock
        ? blockImageUploadMutation.variables?.slot ?? null
        : null,
      needsApply,
      applyBusy,
      onUpload: (slot: string, file: File) =>
        blockImageUploadMutation.mutate({ blockKey: selectedBlock, file, slot }),
      onChoose: (slot: string, url: string) =>
        patchImageTreatment(selectedBlock, slot, { mode: "selected", url }),
      onRemove: (slot: string) =>
        patchImageTreatment(selectedBlock, slot, { mode: "none" }),
      onRestoreAutomatic: (slot: string) =>
        patchImageTreatment(selectedBlock, slot, { mode: "automatic" }),
      onApply: () => regenerateBlocks([selectedBlock]),
    };
  })();

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-muted/20">
      {/* ===== Top bar ===== */}
      <div className="flex items-center gap-3 border-b bg-background px-4 py-2.5 shrink-0 flex-wrap">
        <button
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors shrink-0"
          onClick={() => navigate(backLink)}
          data-testid="link-back"
        >
          <ArrowLeft className="h-4 w-4" /> All emails
        </button>
        <span className="text-muted-foreground/40">/</span>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="h-8 w-56 border-transparent bg-transparent px-1 font-semibold hover:border-border focus:border-border"
          placeholder="Email name"
          data-testid="input-name"
        />
        {isSent ? (
          <Badge className="bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/15 gap-1" data-testid="badge-status">
            <CheckCircle2 className="h-3 w-3" /> Sent to Klaviyo
          </Badge>
        ) : isSending ? (
          <Badge className="bg-amber-500/15 text-amber-600 hover:bg-amber-500/15 gap-1" data-testid="badge-status">
            <Loader2 className="h-3 w-3 animate-spin" /> Sending…
          </Badge>
        ) : status === "send_failed" ? (
          <Badge className="bg-destructive/15 text-destructive hover:bg-destructive/15 gap-1" data-testid="badge-status">
            Send failed
          </Badge>
        ) : (
          <Badge variant="outline" className="text-muted-foreground" data-testid="badge-status">
            Draft
          </Badge>
        )}

        {/* Context chips */}
        <div className="hidden xl:flex items-center gap-1.5 mx-auto">
          <TopChip icon={Users} value={email.personaNameSnapshot || "General voice"} testId="chip-persona" />
          <TopChip icon={Package} value={email.productNameSnapshot || "—"} testId="chip-product" />
          <TopChip icon={email.type === "flow" ? Repeat : Megaphone} value={tag} testId="chip-angle" />
          <TopChip icon={Percent} value={saleText} testId="chip-sale" />
        </div>

        <div className="flex items-center gap-2 ml-auto xl:ml-0">
          <SaveIndicator state={saveState} />
          <Dialog open={subjectModalOpen} onOpenChange={setSubjectModalOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                data-testid="button-subject-preview"
              >
                <Mail className="h-4 w-4" />
                Subject &amp; preview
              </Button>
            </DialogTrigger>
            <DialogContent
              className="max-h-[90vh] w-full max-w-[560px] overflow-y-auto rounded-xl border-border shadow-none"
              data-export-exclude
            >
              <DialogHeader>
                <DialogTitle className="text-base">Subject &amp; preview</DialogTitle>
              </DialogHeader>
              <EmailSubjectPreviewPanel
                subjectLines={subjectLines}
                onChangeSubjectLines={setSubjectLines}
                previewText={previewText}
                onChangePreviewText={setPreviewText}
                regenCount={subjectRegenCount}
                onChangeRegenCount={setSubjectRegenCount}
                feedback={subjectFeedback}
                onChangeFeedback={setSubjectFeedback}
                onRegenerate={() =>
                  subjectRegenMutation.mutate({
                    count: Number(subjectRegenCount),
                    feedback: subjectFeedback,
                  })
                }
                regenerating={subjectRegenMutation.isPending}
                senderName={brandName}
                onDone={() => setSubjectModalOpen(false)}
              />
            </DialogContent>
          </Dialog>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                disabled={regenMutation.isPending}
                data-testid="button-regenerate-menu"
              >
                {regeneratingAll || regenStatusLabel ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                <span data-testid="text-regen-status">
                  {regenStatusLabel ??
                    (regeneratingAll ? "Regenerating…" : "Regenerate")}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[300px]">
              <DropdownMenuLabel>Regenerate</DropdownMenuLabel>
              <DropdownMenuItem
                className="flex-col items-start gap-0.5"
                onClick={() => {
                  setRegenModeFeedback("");
                  setRegenModePrompt("content");
                }}
                data-testid="menu-regen-content"
              >
                <span className="flex items-center gap-2 font-medium">
                  <Sparkles className="h-4 w-4" /> {EMAIL_REGEN_LABELS.content}
                </span>
                <span className="pl-6 text-xs text-muted-foreground">
                  {EMAIL_REGEN_DESCRIPTIONS.content}
                </span>
              </DropdownMenuItem>
              {/* Legacy preset emails re-run the FULL preset copy pass for any
                  regen mode server-side (images are preserved), so only the
                  content entry is offered — layout/images/hero/full stay
                  archetype-only. Block-driven template emails get every mode
                  EXCEPT layout (their layout is fixed; the server 400s it). */}
              {!isLegacyPreset && (
              <>{!isBlockDriven && (
              <DropdownMenuItem
                className="flex-col items-start gap-0.5"
                onClick={() => {
                  setRegenModeFeedback("");
                  setRegenModePrompt("layout");
                }}
                data-testid="menu-regen-layout"
              >
                <span className="flex items-center gap-2 font-medium">
                  <LayoutGrid className="h-4 w-4" /> {EMAIL_REGEN_LABELS.layout}
                </span>
                <span className="pl-6 text-xs text-muted-foreground">
                  {EMAIL_REGEN_DESCRIPTIONS.layout}
                </span>
              </DropdownMenuItem>
              )}
              <DropdownMenuItem
                className="flex-col items-start gap-0.5"
                onClick={() => {
                  setRegenModeFeedback("");
                  setRegenModePrompt("images");
                }}
                data-testid="menu-regen-images"
              >
                <span className="flex items-center gap-2 font-medium">
                  <ImageIcon className="h-4 w-4" /> {EMAIL_REGEN_LABELS.images}
                </span>
                <span className="pl-6 text-xs text-muted-foreground">
                  {EMAIL_REGEN_DESCRIPTIONS.images}
                </span>
              </DropdownMenuItem>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger data-testid="menu-regen-hero">
                  <span className="flex items-center gap-2 font-medium">
                    <Flag className="h-4 w-4" /> {EMAIL_REGEN_LABELS.hero}
                  </span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuItem
                    onClick={() => regenMutation.mutate({ block: "hero", mode: "hero" })}
                    data-testid="menu-regen-hero-surprise"
                  >
                    <Sparkles className="mr-2 h-4 w-4" /> Surprise me
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    Emphasis
                  </DropdownMenuLabel>
                  {EMAIL_HERO_SLANTS.map((slant) => (
                    <DropdownMenuItem
                      key={slant}
                      onClick={() =>
                        regenMutation.mutate({
                          block: "hero",
                          mode: "hero",
                          heroSlant: slant as EmailHeroSlant,
                        })
                      }
                      data-testid={`menu-regen-hero-slant-${slant}`}
                    >
                      {EMAIL_HERO_SLANT_LABELS[slant]}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="flex-col items-start gap-0.5 text-destructive focus:text-destructive"
                onSelect={(e) => {
                  e.preventDefault();
                  setStartOverOpen(true);
                }}
                data-testid="menu-regen-full"
              >
                <span className="flex items-center gap-2 font-medium">
                  <RotateCcw className="h-4 w-4" /> {EMAIL_REGEN_LABELS.full}
                </span>
                <span className="pl-6 text-xs text-muted-foreground">
                  {EMAIL_REGEN_DESCRIPTIONS.full}
                </span>
              </DropdownMenuItem></>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          {/* Task #974: optional-feedback dialog for the Content / Layout /
              Images regen modes. Blank feedback = identical to the old
              one-click behavior. */}
          <Dialog
            open={regenModePrompt !== null}
            onOpenChange={(open) => !open && setRegenModePrompt(null)}
          >
            <DialogContent className="sm:max-w-md" data-testid="dialog-regen-mode-feedback">
              <DialogHeader>
                <DialogTitle>
                  {regenModePrompt ? EMAIL_REGEN_LABELS[regenModePrompt] : ""}
                </DialogTitle>
                <DialogDescription>
                  {regenModePrompt ? EMAIL_REGEN_DESCRIPTIONS[regenModePrompt] : ""}
                </DialogDescription>
              </DialogHeader>
              <Textarea
                rows={3}
                value={regenModeFeedback}
                onChange={(e) => setRegenModeFeedback(e.target.value)}
                placeholder="Optional feedback (e.g. shorter copy, warmer imagery)"
                data-testid="input-feedback-regen-mode"
              />
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setRegenModePrompt(null)}
                  data-testid="button-regen-mode-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (!regenModePrompt) return;
                    regenMutation.mutate({
                      block: null,
                      mode: regenModePrompt,
                      feedback: regenModeFeedback.trim() || undefined,
                    });
                    setRegenModePrompt(null);
                  }}
                  data-testid="button-regen-mode-submit"
                >
                  <Sparkles className="mr-1.5 h-4 w-4" /> Regenerate
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <AlertDialog
            open={startOverOpen}
            onOpenChange={(open) => {
              setStartOverOpen(open);
              if (!open) setStartOverFeedback("");
            }}
          >
            <AlertDialogContent data-export-exclude>
              <AlertDialogHeader>
                <AlertDialogTitle>Rewrite everything?</AlertDialogTitle>
                <AlertDialogDescription>
                  {EMAIL_REGEN_DESCRIPTIONS.full} This will overwrite your
                  hand-edited and locked copy. This can't be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <Textarea
                rows={3}
                value={startOverFeedback}
                onChange={(e) => setStartOverFeedback(e.target.value)}
                placeholder="Optional feedback (e.g. shorter copy, warmer imagery)"
                data-testid="input-feedback-start-over"
              />
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="button-start-over-cancel">
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={() =>
                    regenMutation.mutate({
                      block: null,
                      mode: "full",
                      feedback: startOverFeedback.trim() || undefined,
                    })
                  }
                  data-testid="button-start-over-confirm"
                >
                  Rewrite everything
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            disabled={dupMutation.isPending}
            onClick={() => dupMutation.mutate()}
            data-testid="button-duplicate"
          >
            {dupMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
            Duplicate
          </Button>
          <AlertDialog
            open={confirmingDelete}
            onOpenChange={(open) => {
              if (!deleteMutation.isPending) setConfirmingDelete(open);
            }}
          >
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10"
              disabled={deleteMutation.isPending}
              onClick={() => setConfirmingDelete(true)}
              data-testid="button-delete-email"
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Delete
            </Button>
            <AlertDialogContent data-testid="dialog-delete-email">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this email?</AlertDialogTitle>
                <AlertDialogDescription>
                  {email.flowId
                    ? `"${email.name}" will be removed from its flow and permanently deleted. This can't be undone.`
                    : `"${email.name}" will be permanently deleted. This can't be undone.`}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="button-cancel-delete-email">
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={deleteMutation.isPending}
                  onClick={(ev) => {
                    ev.preventDefault();
                    deleteMutation.mutate();
                  }}
                  data-testid="button-confirm-delete-email"
                >
                  {deleteMutation.isPending ? "Deleting…" : "Delete"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          {polished ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  disabled={isExporting}
                  data-testid="button-export-images"
                >
                  <Download className="h-4 w-4" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => guardPendingThen(downloadPolishedFullPng)}
                  data-testid="menu-export-full-png"
                >
                  Full-length PNG
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => guardPendingThen(downloadPolishedBlocksZip)}
                  data-testid="menu-export-blocks-zip"
                >
                  Block images (ZIP)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              disabled={isExporting}
              onClick={exportImages}
              data-testid="button-export-images"
            >
              {isExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {isExporting ? "Exporting…" : "Export images"}
            </Button>
          )}
          {isFlowMember ? (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() =>
                navigate(
                  `/brands/${brandId}/email-express/flows/${email.flowId}`,
                )
              }
              data-testid="button-open-flow"
            >
              <Repeat className="h-4 w-4" /> Open flow
            </Button>
          ) : (
            <>
            {/* Preemptive missing-link hint (Task #2325): teach the requirement
                before the send fails; clicking jumps to the sidebar field. */}
            {!hasUsableLink && !isSent && (
              <button
                type="button"
                onClick={focusLinkField}
                className="hidden lg:inline-flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400 hover:underline"
                title="Your email needs a destination link before it can be sent. Click to add one."
                data-testid="hint-missing-link"
              >
                <AlertTriangle className="h-3.5 w-3.5" /> Add a link to send
              </button>
            )}
            <Button
              size="sm"
              className={cn(
                "gap-1.5",
                isSent && "bg-emerald-600 hover:bg-emerald-600 text-white",
              )}
              disabled={sendDisabled}
              onClick={() => guardPendingThen(() => sendMutation.mutate())}
              data-testid="button-send-top"
            >
              {sendMutation.isPending || isSending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isSent ? (
                <Check className="h-4 w-4" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {sendMutation.isPending || isSending
                ? "Sending…"
                : isSent
                  ? "Sent to Klaviyo"
                  : status === "send_failed"
                    ? "Retry send"
                    : "Send draft to Klaviyo"}
            </Button>
            </>
          )}
        </div>
      </div>

      {/* ===== Three panels ===== */}
      <div className="flex flex-1 min-h-0">
        {/* ---- Left: Blocks ---- */}
        {blocksPanelCollapsed ? (
          <aside
            className="w-11 shrink-0 border-r bg-background min-h-0 hidden md:flex flex-col items-center gap-3 py-4"
            data-testid="panel-blocks-collapsed"
          >
            <button
              type="button"
              onClick={() => setBlocksCollapsed(false)}
              className="text-muted-foreground hover:text-foreground transition-colors"
              title="Expand blocks panel"
              data-testid="button-expand-blocks"
            >
              <PanelLeftOpen className="h-5 w-5" />
            </button>
            <ListChecks className="h-4 w-4 text-primary" />
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground [writing-mode:vertical-rl] rotate-180">
              Blocks
            </span>
          </aside>
        ) : (
        <aside className="w-[260px] shrink-0 border-r bg-background overflow-y-auto min-h-0 p-4 space-y-5 hidden md:block">
          <div>
            <div className="flex items-center justify-between gap-2 text-sm font-semibold">
              <span className="flex items-center gap-2">
                <ListChecks className="h-4 w-4 text-primary" /> BLOCKS
              </span>
              <button
                type="button"
                onClick={() => setBlocksCollapsed(true)}
                className="text-muted-foreground hover:text-foreground transition-colors"
                title="Collapse blocks panel"
                data-testid="button-collapse-blocks"
              >
                <PanelLeftClose className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {isBlockDriven
                ? "Fixed layout — click a section to edit it."
                : "Drag to reorder. Click to select."}
            </p>
          </div>

          <div className="space-y-1.5">
            {(presetDef ? normalizeEmailPresetBlocks(presetDef, blocks) : blocks).map((key) => {
              // Preset section keys aren't in BLOCK_META — build a row meta
              // from the template definition (locked = pinned, no delete).
              const presetSec = presetDef?.sections.find(
                (s) => s.key === emailBlockType(key),
              );
              const meta =
                BLOCK_META[emailBlockType(key)] ??
                (presetSec
                  ? {
                      label: presetSec.label,
                      icon: LayoutGrid,
                      locked: !!presetSec.locked,
                      regenerable: true,
                    }
                  : undefined);
              if (!meta) return null;
              const Icon = meta.icon;
              const selected = selectedBlock === key;
              const hidden = isHidden(key);
              const editState = blockEditState(key);
              // Non-locked blocks can drag-to-reorder. Block-driven emails
              // reorder over the full list (locked hero/body/footer stay put
              // and don't accept drops).
              const canDrag = !meta.locked;
              return (
                <div
                  key={key}
                  draggable={canDrag}
                  onDragStart={() => canDrag && setDragKey(key)}
                  onDragOver={(e) => canDrag && e.preventDefault()}
                  onDrop={() => canDrag && reorderTo(key)}
                  onClick={() => selectBlock(key)}
                  className={cn(
                    "group flex items-center gap-1.5 rounded-lg border px-2 py-2 cursor-pointer transition-colors",
                    selected ? "border-primary bg-primary/10" : "border-border/60 hover:bg-muted/50",
                    hidden && "opacity-50",
                  )}
                  data-testid={`block-row-${key}`}
                >
                  {canDrag && (
                    <GripVertical
                      className="h-3.5 w-3.5 text-muted-foreground/40 cursor-grab shrink-0"
                      data-testid={`drag-handle-${key}`}
                    />
                  )}
                  <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-sm font-medium truncate flex-1">{labelFor(key)}</span>
                  {editState === "failed" ? (
                    <AlertCircle
                      className="h-3.5 w-3.5 shrink-0 text-destructive"
                      aria-label="Couldn't regenerate — retry"
                      data-testid={`block-failed-${key}`}
                    />
                  ) : editState === "pending" ? (
                    <span
                      className="h-2 w-2 shrink-0 rounded-full bg-amber-500"
                      title="Edited — regenerate to apply"
                      data-testid={`block-pending-dot-${key}`}
                    />
                  ) : null}
                  {meta.locked ? (
                    <Badge
                      variant="secondary"
                      className="h-5 px-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
                      data-testid={`badge-always-${key}`}
                    >
                      always
                    </Badge>
                  ) : (
                    <>
                      <button
                        className="text-muted-foreground/60 hover:text-foreground transition-colors shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleHidden(key);
                        }}
                        title={hidden ? "Show block" : "Hide block"}
                        data-testid={`button-toggle-hide-${key}`}
                      >
                        {hidden ? (
                          <EyeOff className="h-3.5 w-3.5" />
                        ) : (
                          <Eye className="h-3.5 w-3.5" />
                        )}
                      </button>
                      {/* Preset sections can be hidden but never deleted —
                          the template's structure is fixed. */}
                      {canDeleteBlock(key) && (
                        <button
                          className="text-muted-foreground/60 hover:text-destructive transition-colors shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPendingDelete(key);
                          }}
                          title="Delete block"
                          data-testid={`button-delete-${key}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Links (Task #1914): ONE default CTA link for the whole email.
              Individual CTA buttons (hero/body) can override it from the
              right-hand block panel; overrides live in linkConfig.blockLinks
              under the legacy "cta" mode so old drafts keep resolving the
              same links they always did. Sits ABOVE "Add block" (Task #2325)
              so the required field is discoverable, with a warning badge and
              a temporary highlight when the send guard points the user here. */}
          <div
            className={cn(
              "space-y-2.5 border-t pt-4 rounded-lg transition-all duration-300",
              linkFieldHighlight &&
                "ring-2 ring-amber-400 bg-amber-50 dark:bg-amber-950/30 border-transparent p-3",
            )}
            data-testid="section-links"
          >
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Links
              {!hasUsableLink && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-900/50 px-1.5 py-0.5 text-[10px] font-medium normal-case tracking-normal text-amber-700 dark:text-amber-300"
                  data-testid="badge-link-missing"
                >
                  <AlertTriangle className="h-3 w-3" /> Needed to send
                </span>
              )}
            </div>
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">CTA link</span>
              <Input
                ref={linkInputRef}
                value={linkConfig.sitewideUrl || ""}
                onChange={(e) => setLinkConfig((p) => ({ ...p, sitewideUrl: e.target.value }))}
                placeholder="yourbrand.com/sale"
                className="h-8 text-sm"
                data-testid="input-sitewide-link"
              />
            </div>
            <p className="text-[11px] text-muted-foreground" data-testid="text-link-note">
              {hasUsableLink
                ? "You can edit the link per CTA in the email — select the section and change its CTA link."
                : "Where should the email's buttons send people? Required before sending to Klaviyo."}
            </p>
          </div>

          {/* Add an image block (Task #2286): image blocks have no AI copy, so
              inserting one is a plain layout change. Fixed-section preset
              templates can't gain blocks; block-driven emails add repeated
              instances via suffixed ids (slotted at the template position). */}
          {!presetDef && (
            <div className="space-y-1.5">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Add block
              </div>
              <div className="flex flex-col gap-1.5">
                <button
                  type="button"
                  onClick={() => addImageBlock("image_only")}
                  className="flex items-center gap-1.5 rounded-lg border border-dashed border-border/60 px-2 py-2 text-sm text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
                  data-testid="button-add-image-only"
                >
                  <ImageIcon className="h-4 w-4 shrink-0" /> Image
                </button>
                <button
                  type="button"
                  onClick={() => addImageBlock("image_discount")}
                  className="flex items-center gap-1.5 rounded-lg border border-dashed border-border/60 px-2 py-2 text-sm text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
                  data-testid="button-add-image-discount"
                >
                  <Percent className="h-4 w-4 shrink-0" /> Image + discount
                </button>
              </div>
            </div>
          )}

        </aside>
        )}

        {/* ---- Center: Preview ---- */}
        <main
          ref={previewPaneRef}
          className="relative flex-1 min-w-0 min-h-0 overflow-y-auto px-6 pb-6 pt-0 flex flex-col items-center"
        >
          {(editMode || isLegacyPreset) && (
            <EmailGenerationProgress email={email} primaryColor={style.colors.primary} />
          )}
          <div className="w-full max-w-3xl flex items-center justify-between mb-4 mt-6">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Smartphone className="h-4 w-4 text-primary" /> PREVIEW
            </div>
            <div className="flex items-center gap-2">
              {/* No edit-mode toggle (user decision Jul 29 2026): editing is
                  V1-style — click a block in the LEFT sidebar and the RIGHT
                  edit panel opens over the canonical captured/polished view.
                  The raw-editor branches and setEditMode are kept for legacy
                  presets only. */}
              <div className="flex items-center gap-1 rounded-lg border p-0.5">
                <Button
                  variant={previewWidth === "desktop" ? "secondary" : "ghost"}
                  size="sm"
                  className="h-7 gap-1.5"
                  onClick={() => setPreviewWidth("desktop")}
                  data-testid="button-preview-desktop"
                >
                  <Monitor className="h-3.5 w-3.5" /> Desktop
                </Button>
                <Button
                  variant={previewWidth === "mobile" ? "secondary" : "ghost"}
                  size="sm"
                  className="h-7 gap-1.5"
                  onClick={() => setPreviewWidth("mobile")}
                  data-testid="button-preview-mobile"
                >
                  <Smartphone className="h-3.5 w-3.5" /> Mobile
                </Button>
              </div>
            </div>
          </div>
          {showPolishedStack &&
            (queuedChangeCount > 0 ||
              // A queued section regen may not flag a pending block until the
              // server stamps its stale marker on success — show the banner
              // for the in-flight window too so the run is never invisible.
              backgroundFillBusy ||
              (!hasBlockManifest && polishStale)) && (
            <div
              className="w-full max-w-3xl mb-4 flex items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-xs text-amber-800"
              data-testid="polish-stale-note"
            >
              <span className="flex items-center gap-2">
                <Info className="h-3.5 w-3.5 shrink-0" />
                {backgroundFillBusy ? (
                  <span>
                    {regenMutation.variables?.mode === "section"
                      ? "This section is being regenerated — apply the queued changes once it's ready to render the update into the polished email."
                      : "A new section is being written — apply the queued changes once it's ready to render it into the polished email."}
                  </span>
                ) : queuedChangeCount > 0 ? (
                  <span>
                    {pendingBlockKeys.length > 0 && layoutOrderStale
                      ? `${
                          pendingBlockKeys.length === 1
                            ? "1 section has edits"
                            : `${pendingBlockKeys.length} sections have edits`
                        } and the block order changed.`
                      : layoutOrderStale
                        ? "The block order changed."
                        : pendingBlockKeys.length === 1
                          ? "1 section has edits that haven't been regenerated yet."
                          : `${pendingBlockKeys.length} sections have edits that haven't been regenerated yet.`}{" "}
                    Apply the queued changes to update the polished email.
                  </span>
                ) : (
                  <span>
                    Your latest copy edits will render into the polished version
                    on your next layout change or when you send.
                  </span>
                )}
              </span>
              {queuedChangeCount > 0 && hasBlockManifest && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 shrink-0 gap-1.5 border-amber-400 bg-white text-amber-800 hover:bg-amber-100"
                  // Also gated while a just-added section is still generating
                  // its copy (backgroundFillBusy) or images (allContentReady) —
                  // applying mid-generation would render a half-built section.
                  disabled={polishBusy || backgroundFillBusy || !allContentReady}
                  onClick={applyQueuedChanges}
                  data-testid="button-apply-changes-note"
                >
                  {backgroundFillBusy || !allContentReady ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                  )}{" "}
                  Apply changes ({queuedChangeCount})
                </Button>
              )}
            </div>
          )}

          {/* Queued-changes banner (capture flow, polish disabled): edits are
              queued instead of auto-refreshing the captured preview — the user
              applies them explicitly, mirroring V1's polish "Apply changes". */}
          {/* Also shown while a background section regen runs or its image is
              still rendering (!allContentReady) — the regen is never invisible
              and the update stays MANUAL. */}
          {showCapturedStack &&
            (previewQueued || backgroundFillBusy || !allContentReady) && (
            <div
              className="w-full max-w-3xl mb-4 flex items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-xs text-amber-800"
              data-testid="capture-stale-note"
            >
              <span className="flex items-center gap-2">
                <Info className="h-3.5 w-3.5 shrink-0" />
                <span>
                  {backgroundFillBusy || !allContentReady
                    ? "A section is being regenerated — once it's ready, update the preview to render it."
                    : queuedSectionRegens.length > 0
                      ? "An image regeneration is queued — update the preview to run it."
                      : imageRefreshQueued
                        ? "A regenerated image is ready — update the preview to render it."
                        : "You have queued changes that aren't in the preview yet. Update the preview to render them."}
                </span>
              </span>
              <Button
                size="sm"
                variant="outline"
                className="h-7 shrink-0 gap-1.5 border-amber-400 bg-white text-amber-800 hover:bg-amber-100"
                // Wait for the draft to finish saving and any in-flight
                // copy/image generation before capturing, so the preview can't
                // render a half-saved or half-built state.
                disabled={
                  saveState === "saving" ||
                  !allContentReady ||
                  capturing ||
                  backgroundFillBusy
                }
                onClick={approveQueuedAndUpdatePreview}
                data-testid="button-update-preview"
              >
                {saveState === "saving" || !allContentReady || backgroundFillBusy ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5" />
                )}{" "}
                Update preview
              </Button>
            </div>
          )}

          <div className="relative shrink-0">
            {/* While (re)polishing, the ONLY visible thing is an opaque loading
                card — never the editor or a half-built/partial polish. Both the
                editor and any existing polished stack are parked off-screen so
                nothing "in between" can show through. */}
            {canonicalLoading && (
              <PolishingCard
                width={previewDesignW}
                zoom={previewScale}
                phase={
                  // A freshly-duplicated email carries its finished polish by
                  // copying image bytes (stage seeded "copying" — no AI work).
                  email?.polishStage === "copying"
                    ? "copying"
                    : polishBusy
                      ? "polishing"
                      : "designing"
                }
              />
            )}
            {showCapturedStack && (
              <div
                className="bg-white text-neutral-900 rounded-xl shadow-sm ring-1 ring-black/10 overflow-hidden"
                style={{ width: previewDesignW, zoom: previewScale }}
                data-testid="captured-preview"
              >
                {capturing && (
                  <div
                    className="flex items-center justify-center gap-2 border-b bg-neutral-50 px-3 py-1.5 text-[11px] text-muted-foreground"
                    data-testid="preview-update-strip"
                  >
                    <Loader2 className="h-3 w-3 animate-spin" /> Updating preview
                    images…
                  </div>
                )}
                {capturedList
                  // Baked footer images are dropped: the standard live footer
                  // below matches the live-text footer in the sent HTML.
                  .filter((b) => b.blockKey !== "footer" && b.blockKey !== "preset_trust")
                  .map((b) => (
                  <img
                    key={b.blockKey}
                    src={b.url}
                    alt=""
                    className="block w-full"
                    data-testid={`captured-block-${b.blockKey}`}
                  />
                ))}
                {renderPreviewBlock("footer", {
                  brandName,
                  headline,
                  bodyCopy,
                  ctaText,
                  blockCopy,
                  sale: saleText,
                  style,
                  footer: resolveEffectiveEmailFooter(footerContent, footerOverrides, brandFooterSettings),
                  logoExportUrl,
                  designWidth: previewDesignW,
                  // Bottom-edge-sampled color of the last captured image so the
                  // footer flows out of the stack like the sent email.
                  sampledFooter: capturedSampledFooter,
                } as any)}
              </div>
            )}
            {showPolishedStack && (
              <div
                className="bg-white text-neutral-900 rounded-xl shadow-sm ring-1 ring-black/10 overflow-hidden"
                style={{ width: previewDesignW, zoom: previewScale }}
                data-testid="polished-preview"
              >
                {(email.polishBlocks ?? [])
                  // Baked footer images are dropped from the polished stack:
                  // the standard live footer rendered below (brand name +
                  // address + unsubscribe, brand colors) IS the footer now —
                  // identical on every email and matching the sent HTML.
                  .filter((b) => b.blockKey !== "footer" && b.blockKey !== "preset_trust")
                  .map((b) => {
                  const regenerating =
                    scopedPartialRun &&
                    !!b.blockKey &&
                    scopedRunKeys.has(b.blockKey);
                  const failed =
                    !regenerating && !!b.blockKey && !!b.renderError;
                  // Task #2329: clicking the polished hero row selects the hero
                  // (see comment below). Swaps never auto-regenerate — the
                  // server-persisted heroSwapPending flag drives an explicit
                  // "Apply new image" button in the sidebar panel.
                  // hero so the edit-panel picker (upload / choose / AI
                  // regenerate) opens in the right sidebar — no on-canvas
                  // overlay here by user request. A successful swap
                  // re-polishes just the hero (repolishHeroAfterSwap).
                  const isHeroRow = b.blockKey === "hero";
                  return (
                    <div
                      key={b.index}
                      className={
                        "relative"
                      }
                      onClick={
                        isHeroRow
                          ? () => setSelectedBlock("hero")
                          : undefined
                      }
                    >
                      <img
                        src={b.url}
                        alt={b.alt || ""}
                        className="block w-full"
                        data-testid={`polished-block-${b.index}`}
                      />
                      {regenerating && (
                        <div
                          className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-[2px]"
                          data-testid={`polished-block-loading-${b.index}`}
                        >
                          <div className="flex items-center gap-2 rounded-full bg-white/95 px-3 py-1.5 text-xs font-medium text-muted-foreground shadow ring-1 ring-black/10">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Regenerating this section…
                          </div>
                        </div>
                      )}
                      {failed && (
                        <div
                          className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/75 backdrop-blur-[1px]"
                          data-testid={`polished-block-failed-${b.index}`}
                        >
                          <div className="flex items-center gap-2 rounded-full bg-white/95 px-3 py-1.5 text-xs font-medium text-destructive shadow ring-1 ring-destructive/20">
                            <AlertCircle className="h-3.5 w-3.5" />
                            Couldn't regenerate
                          </div>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="h-7 gap-1.5"
                            disabled={polishBusy}
                            onClick={() => regenerateBlocks([b.blockKey!])}
                            data-testid={`button-retry-block-${b.index}`}
                          >
                            <RefreshCw className="h-3.5 w-3.5" /> Retry section
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
                {/* Standard live footer under the polished stack — matches the
                    live-text footer appended to the sent HTML. */}
                {renderPreviewBlock("footer", {
                  brandName,
                  headline,
                  bodyCopy,
                  ctaText,
                  blockCopy,
                  sale: saleText,
                  style,
                  footer: resolveEffectiveEmailFooter(footerContent, footerOverrides, brandFooterSettings),
                  logoExportUrl,
                  designWidth: previewDesignW,
                  // Bottom-edge-sampled color of the last polished image so the
                  // footer flows out of the stack like the sent email.
                  sampledFooter: polishedSampledFooter,
                } as any)}
              </div>
            )}
            {/* Off-screen parking lives on a WRAPPER so the captured node
                (previewRef) keeps a STATIC position. html-to-image renders the
                captured node's own CSS offset INSIDE the SVG foreignObject, so a
                negative `left` on previewRef itself shifts every child off-canvas
                → a blank white capture (and therefore a blank polish). Keep the
                offset on the wrapper; the inner node is always at left:0. */}
            <div
              style={
                showRawPreview
                  ? { width: previewDesignW }
                  : { position: "absolute", left: -99999, top: 0, width: previewDesignW }
              }
              aria-hidden={!showRawPreview || undefined}
            >
            <div
              ref={previewRef}
              className="bg-white text-neutral-900 rounded-xl shadow-sm ring-1 ring-black/10 overflow-hidden"
              style={{ width: previewDesignW, zoom: previewScale }}
              data-testid="email-preview"
            >
            {visibleBlocks.map((key) => {
              // Per-block progressive-load gating: while the background copy
              // worker is filling this block in (or it failed), show a skeleton
              // / inline failed state instead of the rendered block. Legacy
              // emails (no blockCopyStatus) resolve to undefined → "ready".
              const blockStatus = email.blockCopyStatus?.[key];
              const blockNode =
                blockStatus === "pending" ? (
                  <EmailBlockSkeleton
                    label={labelFor(key)}
                    blockKey={key}
                    shimmerBase={tintHexToWhite(style.colors.primary, 0.82)}
                    shimmerGlow={tintHexToWhite(style.colors.primary, 0.93)}
                  />
                ) : blockStatus === "failed" ? (
                  <EmailBlockFailed
                    label={labelFor(key)}
                    message={email.copyError}
                    onRetry={() => retryCopyMutation.mutate()}
                    retrying={retryCopyMutation.isPending}
                  />
                ) : (
                  <div
                    className={blockStatus === "ready" && !isExporting ? "email-block-reveal" : undefined}
                  >
                  {renderPreviewBlock(key, {
                    brandName,
                    headline,
                    bodyCopy,
                    ctaText,
                    blockCopy,
                    sale: saleText,
                    style,
                    footer: resolveEffectiveEmailFooter(footerContent, footerOverrides, brandFooterSettings),
                    logoExportUrl,
                    designWidth: previewDesignW,
                    heroImageUrl: email.heroImageUrl,
                    heroImageStatus: email.heroImageStatus,
                    heroExportUrl,
                    usVsThemImageUrl: email.usVsThemImageUrl,
                    usVsThemImageStatus: email.usVsThemImageStatus,
                    usVsThemExportUrl,
                    comparisonTableImageUrl: email.comparisonTableImageUrl,
                    comparisonTableImageStatus: email.comparisonTableImageStatus,
                    comparisonTableExportUrl,
                    productImageUrls: email.productImageUrls ?? undefined,
                    productListImages: email.productListImages ?? undefined,
                    productExportUrls,
                    productImageExportUrls,
                    blockImageExportUrls,
                    sellingPointsImageUrls: Object.fromEntries(
                      (email.sellingPointsImages ?? []).map((img) => [
                        img.instanceId,
                        img.url,
                      ]),
                    ),
                    sellingPointsImageStatus: Object.fromEntries(
                      (email.sellingPointsImages ?? []).map((img) => [
                        img.instanceId,
                        img.status,
                      ]),
                    ),
                    sellingPointsExportUrls,
                    didYouKnowImageUrls: Object.fromEntries(
                      (email.didYouKnowImages ?? []).map((img) => [
                        img.instanceId,
                        img.url,
                      ]),
                    ),
                    didYouKnowImageStatus: Object.fromEntries(
                      (email.didYouKnowImages ?? []).map((img) => [
                        img.instanceId,
                        img.status,
                      ]),
                    ),
                    didYouKnowExportUrls,
                    rasterizedBlocks,
                    presetTemplateKey: email.presetTemplateKey ?? null,
                    presetData: email.presetData ?? null,
                    presetImageUrls: Object.fromEntries(
                      (email.presetImages ?? []).map((img) => [
                        img.instanceId,
                        img.url,
                      ]),
                    ),
                    presetImageStatus: Object.fromEntries(
                      (email.presetImages ?? []).map((img) => [
                        img.instanceId,
                        img.status,
                      ]),
                    ),
                    presetExportUrls,
                    presetUrlExportUrls,
                    // Inline hero editing ONLY off the export path, so the editing
                    // chrome can never be captured into the sent email image.
                    editor: isExporting ? undefined : heroEditor,
                  })}
                  </div>
                );
              return (
                <PreviewBlock
                  key={key}
                  blockKey={key}
                  label={labelFor(key)}
                  selected={selectedBlock === key && !isExporting}
                  onSelect={() => setSelectedBlock(key)}
                >
                  {blockNode}
                </PreviewBlock>
              );
            })}
            </div>
            </div>
            {!editMode && !isLegacyPreset && polishErrored && (
              <div
                className="flex min-h-[480px] flex-col items-center justify-center gap-3 rounded-xl bg-white p-6 text-center shadow-sm ring-1 ring-black/10"
                style={{ width: previewDesignW, zoom: previewScale }}
                data-testid="polish-failed"
              >
                <p className="text-sm font-medium text-destructive">
                  Polishing failed
                </p>
                <p className="max-w-xs text-xs text-muted-foreground">
                  {friendlyPolishErrorText(
                    polishLocalError || email.polishError,
                  ) || "Something went wrong while polishing this email."}
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  disabled={polishBusy}
                  onClick={retryPolish}
                  data-testid="button-polish-retry"
                >
                  <Wand2 className="h-4 w-4" /> Try again
                </Button>
              </div>
            )}
          </div>
          <p className="mt-4 shrink-0 text-xs text-muted-foreground" data-testid="text-preview-hint">
            {isLegacyPreset
              ? "This template renders exactly as designed — click any section to edit its copy. What you see is what will be sent."
              : editMode
                ? "Editing the draft — click any block to edit its copy. Changes render into the polished email on your next layout change or send."
                : "Polished — this is exactly what will be downloaded and sent."}
          </p>
        </main>

        {/* ---- Right: per-block edit panel (canonical views: polished OR the
             captured-image stack — V1-style: pick a block on the left, edit its
             copy on the right; captured emails then apply via Update preview) ---- */}
        {!editMode &&
          (polished || showCapturedStack || scopedPartialRun) &&
          selectedBlock &&
          visibleBlocks.includes(selectedBlock) && (
            <EmailBlockEditPanel
              key={selectedBlock}
              blockKey={selectedBlock}
              label={labelFor(selectedBlock)}
              state={blockEditState(selectedBlock)}
              errorMessage={
                friendlyPolishErrorText(
                  manifestByKey.get(selectedBlock)?.renderError,
                ) || undefined
              }
              // Only one polish run at a time: while a scoped run is in
              // flight the other sections stay EDITABLE (copy edits queue as
              // pending changes) but their Regenerate action waits.
              // The FOOTER refresh doesn't require a keyed manifest: the
              // server reconstructs legacy per-block keys itself (and says so
              // if it can't), so gating it on hasBlockManifest just left the
              // button permanently dead on older emails.
              canRegenerate={
                (hasBlockManifest ||
                  (polished && emailBlockType(selectedBlock) === "footer")) &&
                !polishBusy
              }
              headline={headline}
              ctaText={ctaText}
              bodyCopy={bodyCopy}
              copy={resolveEmailInstanceCopy(blockCopy, selectedBlock)}
              punchline={blockCopy.punchlines?.[selectedBlock] ?? ""}
              availableReviews={availableReviews}
              footer={{
                content: resolveEffectiveEmailFooter(
                  footerContent,
                  footerOverrides,
                  brandFooterSettings,
                ),
                ...heroEditor.footer!,
              }}
              onHeadline={setHeadline}
              onCtaText={setCtaText}
              onBodyCopy={setBodyCopy}
              onPunchline={(v) => setInstancePunchline(selectedBlock, v)}
              onPatch={(patch) => patchInstanceCopy(selectedBlock, patch)}
              onRegenerate={(feedback) =>
                regenerateBlocks([selectedBlock], feedback)
              }
              onClose={() => setSelectedBlock("")}
              ctaLink={
                emailIsCtaBlock(selectedBlock)
                  ? {
                      defaultUrl:
                        normalizeEmailLinkConfig(linkConfig).sitewideUrl,
                      overrideUrl: ctaLinkOverrideFor(selectedBlock),
                      onChange: (url) =>
                        setCtaLinkOverride(selectedBlock, url),
                    }
                  : undefined
              }
              isLocked={
                Array.isArray(email.lockedBlocks) &&
                email.lockedBlocks.includes(selectedBlock)
              }
              isEdited={
                Array.isArray(email.userEditedFields) &&
                (email.userEditedFields.includes(
                  emailEditedBlockKey(selectedBlock),
                ) ||
                  (selectedBlock === "hero" &&
                    email.userEditedFields.some((k) =>
                      ["headline", "bodyCopy", "ctaText"].includes(k),
                    )))
              }
              lockBusy={lockBlockMutation.isPending}
              clearBusy={clearEditsMutation.isPending}
              sectionBusy={
                regenMutation.isPending &&
                regenMutation.variables?.mode === "section" &&
                regenMutation.variables?.block === selectedBlock
              }
              onToggleLock={(locked) =>
                lockBlockMutation.mutate({ block: selectedBlock, locked })
              }
              onClearEdits={() =>
                clearEditsMutation.mutate({ block: selectedBlock })
              }
              blockImage={
                (emailBlockType(selectedBlock) === "image_only" ||
                  emailBlockType(selectedBlock) === "image_discount")
                  ? {
                      photos: email.productImageUrls ?? [],
                      currentUrl:
                        resolveEmailInstanceCopy(blockCopy, selectedBlock)
                          .imageUrl ||
                        (email.productImageUrls ?? [])[0] ||
                        "",
                      hasSelection: !!resolveEmailInstanceCopy(
                        blockCopy,
                        selectedBlock,
                      ).imageUrl,
                      busy:
                        blockImageUploadMutation.isPending &&
                        blockImageUploadMutation.variables?.blockKey ===
                          selectedBlock,
                      onUpload: (file) =>
                        blockImageUploadMutation.mutate({
                          blockKey: selectedBlock,
                          file,
                        }),
                      onChoose: (url) =>
                        patchInstanceCopy(selectedBlock, { imageUrl: url }),
                      onReset: () =>
                        patchInstanceCopy(selectedBlock, {
                          imageUrl: undefined,
                        }),
                    }
                  : undefined
              }
              bodyImage={
                emailBlockType(selectedBlock) === "body"
                  ? {
                      photos: email.productImageUrls ?? [],
                      preference: bodyImagePreference,
                      hasImage: bodyHasRenderedImage,
                       checkingImage:
                         shouldInspectLegacyBodyImage &&
                         checkingLegacyBodyImage,
                      busy:
                        bodyImageApplyBusy ||
                        (blockImageUploadMutation.isPending &&
                          blockImageUploadMutation.variables?.blockKey ===
                            "body"),
                      needsApply: bodyImageNeedsApply,
                      applyBusy: bodyImageApplyBusy,
                      onUpload: (file) =>
                        blockImageUploadMutation.mutate({
                          blockKey: "body",
                          file,
                        }),
                      onChoose: (url) =>
                        patchInstanceCopy("body", {
                          bodyImage: { mode: "selected", url },
                        }),
                      onRemove: () =>
                        patchInstanceCopy("body", {
                          bodyImage: { mode: "none" },
                        }),
                      onRestoreAutomatic: () =>
                        patchInstanceCopy("body", {
                          bodyImage: { mode: "automatic" },
                        }),
                      onApply: () => regenerateBlocks(["body"]),
                    }
                  : undefined
              }
              heroImage={
                emailBlockType(selectedBlock) === "hero"
                  ? {
                      photos: email.productImageUrls ?? [],
                      currentUrl: email.heroImageUrl ?? "",
                      // Central busy (mutations + pending AI render) PLUS the
                      // apply/polish run: while an apply is in flight every
                      // hero control locks so a re-select can't race the
                      // running worker.
                      busy: heroSwapState.controlsBusy,
                      regenBusy:
                        heroImageRegenMutation.isPending ||
                        email.heroImageStatus === "pending" ||
                        heroSwapState.applyBusy,
                      onUpload: (file) => heroUploadMutation.mutate(file),
                      onChoose: (url) => heroSetMutation.mutate(url),
                      onRegenerate: (feedback) =>
                        heroImageRegenMutation.mutate(feedback || undefined),
                      // Swaps never auto-regenerate the polished hero — the
                      // user applies the new image explicitly. Server-persisted
                      // flag: survives reloads; failed applies keep it set.
                      needsApply: heroSwapState.needsApply,
                      applyBusy: heroSwapState.applyBusy,
                      onApply: applyHeroSwap,
                    }
                  : undefined
              }
              blockBodyImage={blockBodyImageProp}
              onRegenerateSection={(scope, feedback) => {
                // Captured-preview emails: image-scope regens are QUEUED — the
                // user approves them with "Update preview", which is what
                // actually kicks the regeneration (then auto-recaptures).
                if (showCapturedStack && scope === "image") {
                  setQueuedSectionRegens((prev) => [
                    ...prev.filter((q) => q.block !== selectedBlock),
                    {
                      block: selectedBlock,
                      scope,
                      feedback: feedback || undefined,
                    },
                  ]);
                  toast({
                    title: "Image regeneration queued",
                    description:
                      "Click Update preview to regenerate and render it.",
                  });
                  return;
                }
                regenMutation.mutate({
                  block: selectedBlock,
                  mode: "section",
                  scope,
                  feedback: feedback || undefined,
                  // On a polished per-block email the regen runs in the
                  // background (preservePolish): the polished preview stays
                  // up and the section queues in the pending-changes banner —
                  // Apply runs the partial re-polish. Legacy manifests (no
                  // per-block chunks) keep the old full-loader invalidate.
                  // Captured-preview emails (polish disabled) also run in the
                  // background: the captured stack stays up, the banner shows
                  // "regenerating", and the user applies via Update preview —
                  // never an unprompted whole-preview loader.
                  background: hasBlockManifest || showCapturedStack,
                });
              }}
            />
          )}

      </div>

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <AlertDialogContent data-testid="dialog-delete-block">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this block?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete
                ? `"${labelFor(pendingDelete)}" will be removed from this email.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (pendingDelete) removeBlock(pendingDelete);
                setPendingDelete(null);
              }}
              data-testid="button-confirm-delete"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Soft guard before export/send when there are un-regenerated edits. */}
      <AlertDialog
        open={pendingActionGuard !== null}
        onOpenChange={(open) => {
          if (!open) setPendingActionGuard(null);
        }}
      >
        <AlertDialogContent data-testid="dialog-pending-changes">
          <AlertDialogHeader>
            <AlertDialogTitle>Apply your edits first?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingBlockKeys.length > 0 && layoutOrderStale
                ? `${
                    pendingBlockKeys.length === 1
                      ? "1 section has edits"
                      : `${pendingBlockKeys.length} sections have edits`
                  } and the block order changed.`
                : layoutOrderStale
                  ? "The block order changed but the polished version hasn't been updated yet."
                  : pendingBlockKeys.length === 1
                    ? "1 section has edits that haven't been regenerated yet."
                    : `${pendingBlockKeys.length} sections have edits that haven't been regenerated yet.`}{" "}
              The polished version you're about to export or send won't include
              those changes until you apply them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-pending">
              Cancel
            </AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => {
                const run = pendingActionGuard;
                setPendingActionGuard(null);
                run?.();
              }}
              data-testid="button-continue-anyway"
            >
              Continue anyway
            </Button>
            <AlertDialogAction
              onClick={() => {
                setPendingActionGuard(null);
                applyQueuedChanges();
              }}
              // Same gate as the banner button: applying while a just-added
              // section is still generating would render a half-built section.
              disabled={backgroundFillBusy || !allContentReady}
              data-testid="button-apply-then"
            >
              Apply changes ({queuedChangeCount})
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// =============================================================================
// Top-bar context chip + save indicator
// =============================================================================
function TopChip({ icon: Icon, value, testId }: { icon: any; value: string; testId: string }) {
  return (
    <div
      className="flex items-center gap-1.5 rounded-md border border-border/60 bg-muted/40 px-2 py-1 max-w-[160px]"
      data-testid={testId}
    >
      <Icon className="h-3 w-3 text-muted-foreground shrink-0" />
      <span className="text-xs truncate">{value}</span>
    </div>
  );
}

function SaveIndicator({ state }: { state: "saved" | "saving" | "error" }) {
  if (state === "saving") {
    return (
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="text-save-state">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…
      </span>
    );
  }
  if (state === "error") {
    return (
      <span className="flex items-center gap-1.5 text-xs text-destructive" data-testid="text-save-state">
        Unsaved
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1.5 text-xs text-emerald-600" data-testid="text-save-state">
      <Check className="h-3.5 w-3.5" /> Saved
    </span>
  );
}

// In-flow loading card shown (as the ONLY visible preview content) while a
// polish is in flight. Rotates reassuring status copy with an indeterminate
// progress bar — and deliberately shows NO intermediate AI image or underlying
// editor, so the user only ever sees the validated final blocks.
function PolishingCard({
  width,
  zoom,
  phase = "polishing",
}: {
  width: number;
  zoom: number;
  phase?: "designing" | "polishing" | "copying";
}) {
  return (
    <div
      className="flex min-h-[480px] flex-col items-center justify-center gap-4 rounded-xl bg-white p-6 text-center shadow-sm ring-1 ring-black/10"
      style={{ width, zoom }}
      data-testid="polish-overlay"
    >
      <div className="relative flex h-12 w-12 items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary/30" />
        <Wand2 className="absolute h-5 w-5 text-primary" />
      </div>
      <p
        className="text-sm font-medium text-neutral-800"
        data-testid="text-polish-status"
      >
        {phase === "copying"
          ? "Copying email…"
          : phase === "designing"
            ? "Building your email…"
            : "Polishing…"}
      </p>
      <div className="h-1 w-40 overflow-hidden rounded-full bg-primary/15">
        <div className="h-full w-1/3 animate-pulse rounded-full bg-primary" />
      </div>
    </div>
  );
}
